import { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-hot-toast';
import { useLocation } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { SystemBackup } from '../components/SystemBackup';
import { AnalyticsDashboard } from '../components/AnalyticsDashboard';
import { useSettings } from '../hooks/useSettings';
import { SuperAdminNav } from '../components/SuperAdminNav';
import { useTheme } from '../contexts/ThemeContext';
import { FileUpload } from '../components/FileUpload/FileUpload';
import { useAuth } from '../contexts/AuthContext';
import { usePageActions } from '../contexts/PageActionsContext';

interface Tenant {
  id: string;
  slug: string;
  name: string;
  domain?: string;
  active: boolean;
  plataforma?: string;
  idPlataforma?: string;
  platformId?: string | null;
  platform?: {
    id: string;
    name: string;
    value: string;
    url: string;
    active: boolean;
  } | null;
  allowWhatsWeb?: boolean;
  allowOfficialApi?: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: {
    users: number;
    contacts: number;
    campaigns: number;
    whatsappSessions: number;
  };
  quota?: {
    maxUsers: number;
    maxContacts: number;
    maxCampaigns: number;
    maxConnections: number;
    dispatchLimit?: number | null; // null = ilimitado
  };
}

interface User {
  id: string;
  nome: string;
  email: string;
  role: string;
  ativo: boolean;
  ultimoLogin?: string | null;
  criadoEm: string;
  atualizadoEm: string;
  tenant?: {
    id: string;
    name: string;
    slug: string;
    active: boolean;
  } | null;
}

interface TenantFormData {
  name: string;
  platformId?: string;
  idPlataforma?: string;
  allowWhatsWeb?: boolean;
  allowOfficialApi?: boolean;
  adminUser: {
    nome: string;
    email: string;
    senha: string;
  };
  quotas: {
    maxUsers: string;
    maxContacts: string;
    maxCampaigns: string;
    maxConnections: string;
    dispatchLimit: string; // Limite de disparos (vazio = ilimitado)
  };
}

interface UserFormData {
  nome: string;
  email: string;
  senha: string;
  role: 'SUPERADMIN' | 'ADMIN' | 'USER';
  tenantId?: string; // Mantido para compatibilidade
  tenantIds?: string[]; // Novo: múltiplos tenants
  ativo: boolean;
}

interface SystemStats {
  tenants: {
    total: number;
    active: number;
    inactive: number;
  };
  users: {
    total: number;
    byRole: {
      SUPERADMIN: number;
      ADMIN: number;
      USER: number;
    };
  };
  resources: {
    totalContacts: number;
    totalCampaigns: number;
    totalSessions: number;
    totalMessages: number;
  };
  activity: {
    activeToday: number;
    activeCampaigns: number;
    workingSessions: number;
  };
}

interface BackupInfo {
  tenantId: string;
  tenantSlug: string;
  size: number;
  createdAt: string;
  status: 'success' | 'failed' | 'in_progress';
  error?: string;
}

interface BackupStats {
  totalTenants: number;
  totalBackups: number;
  totalSize: number;
  scheduledJobs: number;
  tenantStats: {
    tenantId: string;
    tenantSlug: string;
    tenantName: string;
    backupCount: number;
    totalSize: number;
    lastBackup: string | null;
    isScheduled: boolean;
  }[];
}


interface Settings {
  id: string;
  wahaHost: string;
  wahaApiKey: string;
  evolutionHost: string;
  evolutionApiKey: string;
  logoUrl?: string;
  companyName?: string;
  faviconUrl?: string;
  pageTitle?: string;
  iconUrl?: string;
  openaiApiKey?: string;
  groqApiKey?: string;
}

const optionalSettingsString = z.preprocess(
  (value) => (typeof value === 'string' ? value : ''),
  z.string()
);

const settingsSchema = z.object({
  wahaHost: optionalSettingsString.refine((val) => !val || z.string().url().safeParse(val).success, {
    message: 'Host deve ser uma URL válida ou vazio'
  }),
  wahaApiKey: optionalSettingsString.refine((val) => !val || val.length >= 10, {
    message: 'API Key deve ter pelo menos 10 caracteres ou estar vazia'
  }),
  evolutionHost: optionalSettingsString.refine((val) => !val || z.string().url().safeParse(val).success, {
    message: 'Host deve ser uma URL válida ou vazio'
  }),
  evolutionApiKey: optionalSettingsString.refine((val) => !val || val.length >= 10, {
    message: 'API Key deve ter pelo menos 10 caracteres ou estar vazia'
  }),
  openaiApiKey: optionalSettingsString.refine((val) => !val || val.length >= 10, {
    message: 'API Key da OpenAI deve ter pelo menos 10 caracteres ou estar vazia'
  }),
  groqApiKey: optionalSettingsString.refine((val) => !val || val.length >= 10, {
    message: 'API Key da Groq deve ter pelo menos 10 caracteres ou estar vazia'
  }),
});

const generalSettingsSchema = z.object({
  companyName: z.string().refine((val) => !val || (val.length >= 1 && val.length <= 100), {
    message: 'Nome da empresa deve ter entre 1 e 100 caracteres ou estar vazio'
  }),
  pageTitle: z.string().refine((val) => !val || (val.length >= 1 && val.length <= 100), {
    message: 'Título da página deve ter entre 1 e 100 caracteres ou estar vazio'
  }),
});

type SettingsFormData = z.infer<typeof settingsSchema>;
type GeneralSettingsFormData = z.infer<typeof generalSettingsSchema>;

interface Platform {
  id: string;
  name: string;
  value: string;
  color: string;
  icon?: string | null;
  description?: string | null;
  url: string;
  adminToken: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

interface PlatformOption {
  id: string;
  name: string;
  value: string;
  url: string;
  active: boolean;
}

interface PlatformFormData {
  name: string;
  value: string;
  color: string;
  icon: string;
  description: string;
  url: string;
  adminToken: string;
}

interface Tooltip {
  id: string;
  identificador: string;
  titulo: string;
  localizacao: string;
  mensagem: string;
  linkSuporte?: string | null;
  videoPlataforma?: string | null;
  videoTitulo?: string | null;
  videoUrl?: string | null;
  videoDescricao?: string | null;
  videoAnexoUrl?: string | null;
  videoAnexoNome?: string | null;
  ativo: boolean;
  ordem: number;
  createdAt: string;
  updatedAt: string;
}

interface SuperAdminManagerPageProps {
  initialTab?: 'general' | 'appearance' | 'integrations' | 'ai' | 'tenants' | 'users' | 'backup' | 'platforms' | 'tooltips';
}

export function SuperAdminManagerPage({ initialTab = 'general' }: SuperAdminManagerPageProps) {
  console.log(`%c[PAI Layout] SuperAdminManagerPage Renderizou`, 'color: #ffaa00; font-weight: bold;');
  const { theme } = useTheme();
  const { isUrlLogin } = useAuth();
  const { registerMetadata } = usePageActions();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'general' | 'appearance' | 'integrations' | 'ai' | 'tenants' | 'users' | 'backup' | 'platforms' | 'tooltips'>(initialTab);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [backupStats, setBackupStats] = useState<BackupStats | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Paginação e filtros para usuários
  const [userSearch, setUserSearch] = useState('');
  const [userPage, setUserPage] = useState(1);
  const [userTotalPages, setUserTotalPages] = useState(1);
  const [userTotal, setUserTotal] = useState(0);
  const userPageSize = 20;
  const [backupLoading, setBackupLoading] = useState(false);

  // Filtros para empresas
  const [tenantSearchName, setTenantSearchName] = useState('');
  const [tenantFilterPlatform, setTenantFilterPlatform] = useState('');
  const [tenantSearchPlatformId, setTenantSearchPlatformId] = useState('');

  // Plataformas únicas para o dropdown
  const uniquePlatforms = useMemo(() => {
    const platforms = tenants
      .map(t => t.plataforma)
      .filter((p): p is string => !!p && p.length > 0);
    return [...new Set(platforms)].sort();
  }, [tenants]);

  // Tenants filtrados
  const filteredTenants = useMemo(() => {
    return tenants.filter(tenant => {
      // Filtro por nome
      if (tenantSearchName) {
        const search = tenantSearchName.toLowerCase().trim();
        const matchName = tenant.name.toLowerCase().includes(search);
        const matchSlug = tenant.slug.toLowerCase().includes(search);
        if (!matchName && !matchSlug) return false;
      }
      // Filtro por plataforma
      if (tenantFilterPlatform) {
        if ((tenant.plataforma || '') !== tenantFilterPlatform) return false;
      }
      // Filtro por ID da plataforma
      if (tenantSearchPlatformId) {
        const searchId = tenantSearchPlatformId.trim();
        if (!tenant.idPlataforma || !tenant.idPlataforma.includes(searchId)) return false;
      }
      return true;
    });
  }, [tenants, tenantSearchName, tenantFilterPlatform, tenantSearchPlatformId]);

  const hasActiveTenantFilters = tenantSearchName || tenantFilterPlatform || tenantSearchPlatformId;

  const clearTenantFilters = () => {
    setTenantSearchName('');
    setTenantFilterPlatform('');
    setTenantSearchPlatformId('');
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [availableTenants, setAvailableTenants] = useState<Tenant[]>([]);

  // Platforms state
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [platformOptions, setPlatformOptions] = useState<PlatformOption[]>([]);
  const [isPlatformModalOpen, setIsPlatformModalOpen] = useState(false);
  const [editingPlatform, setEditingPlatform] = useState<Platform | null>(null);
  const [platformFormData, setPlatformFormData] = useState<PlatformFormData>({
    name: '', value: '', color: '#3b82f6', icon: '', description: '', url: '', adminToken: ''
  });
  const [showPlatformToken, setShowPlatformToken] = useState(false);
  const [revealedTokens, setRevealedTokens] = useState<Record<string, string>>({});

  // Tooltips state
  const [tooltips, setTooltips] = useState<Tooltip[]>([]);
  const [tooltipSearch, setTooltipSearch] = useState('');
  const [tooltipFilterSector, setTooltipFilterSector] = useState('');
  const [tooltipFilterStatus, setTooltipFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [expandedTooltipId, setExpandedTooltipId] = useState<string | null>(null);
  const [savingTooltipId, setSavingTooltipId] = useState<string | null>(null);
  const [uploadingAttachmentId, setUploadingAttachmentId] = useState<string | null>(null);
  const [formData, setFormData] = useState<TenantFormData>({
    name: '',
    platformId: '',
    idPlataforma: '',
    allowWhatsWeb: true,
    allowOfficialApi: true,
    adminUser: {
      nome: '',
      email: '',
      senha: ''
    },
    quotas: {
      maxUsers: '10',
      maxContacts: '1000',
      maxCampaigns: '50',
      maxConnections: '5',
      dispatchLimit: '' // Vazio = ilimitado
    }
  });
  const [userFormData, setUserFormData] = useState<UserFormData>({
    nome: '',
    email: '',
    senha: '',
    role: 'USER',
    tenantId: '',
    tenantIds: [],
    ativo: true
  });
  const [tenantSearchQuery, setTenantSearchQuery] = useState('');
  
  // Password visibility states
  const [showAdminPassword, setShowAdminPassword] = useState(false);
  const [showUserPassword, setShowUserPassword] = useState(false);
  const [showWahaApiKey, setShowWahaApiKey] = useState(false);
  const [showEvolutionApiKey, setShowEvolutionApiKey] = useState(false);
  const [showOpenAIApiKey, setShowOpenAIApiKey] = useState(false);
  const [showGroqApiKey, setShowGroqApiKey] = useState(false);
  const [activeModal, setActiveModal] = useState<'waha' | 'evolution' | 'openai' | 'groq' | null>(null);
  const [integrationSettings, setIntegrationSettings] = useState<Settings | null>(null);
  const [generalSettings, setGeneralSettings] = useState<Settings | null>(null);
  const [generalSaveMessage, setGeneralSaveMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [faviconFile, setFaviconFile] = useState<File | null>(null);
  const [faviconPreview, setFaviconPreview] = useState<string>('');
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const [iconFile, setIconFile] = useState<File | null>(null);
  const [iconPreview, setIconPreview] = useState<string>('');
  const [uploadingIcon, setUploadingIcon] = useState(false);

  const wahaLogoSrc = theme === 'dark' ? '/assets/logos/integrations/wahaWhite.png' : '/assets/logos/integrations/waha.png';
  const evolutionLogoSrc = theme === 'dark' ? '/assets/logos/integrations/evolutionapiWhite.png' : '/assets/logos/integrations/evolutionapi.png';

  // Sincronizar activeTab com a rota atual quando a URL mudar
  useEffect(() => {
    const pathToTabMap: Record<string, 'general' | 'appearance' | 'integrations' | 'ai' | 'tenants' | 'users' | 'backup' | 'platforms' | 'tooltips'> = {
      '/superadmin/configuracoes': 'general',
      '/superadmin/aparencia': 'appearance',
      '/superadmin/integracoes': 'integrations',
      '/superadmin/ia': 'ai',
      '/superadmin/empresas': 'tenants',
      '/superadmin/plataformas': 'platforms',
      '/superadmin/tooltips': 'tooltips',
      '/superadmin/usuarios': 'users',
      '/superadmin/backup': 'backup',
    };

    const currentTab = pathToTabMap[location.pathname];
    if (currentTab && currentTab !== activeTab) {
      console.log(`%c[SuperAdminManagerPage] Sincronizando tab: ${currentTab} (rota: ${location.pathname})`, 'color: #00ff00; font-weight: bold;');
      setActiveTab(currentTab);
    }
  }, [location.pathname, activeTab]);

  // Register metadata for iframe mode
  useEffect(() => {
    if (isUrlLogin) {
      registerMetadata('/superadmin', {
        title: 'Super Admin',
        subtitle: 'Gerenciamento completo do sistema'
      });
    }
  }, [isUrlLogin, registerMetadata]);

  useEffect(() => {
    loadData();
    if (activeTab === 'integrations' || activeTab === 'ai') {
      loadIntegrationSettings();
    }
    if (activeTab === 'general' || activeTab === 'appearance') {
      loadGeneralSettings();
    }
    if (activeTab === 'platforms') {
      loadPlatforms();
    }
  }, [activeTab]);

  // Recarregar usuários quando página ou busca mudar
  useEffect(() => {
    if (activeTab === 'users') {
      loadData();
    }
  }, [userPage, userSearch]);

  const {
    register,
    handleSubmit: handleFormSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<SettingsFormData>({
    resolver: zodResolver(settingsSchema),
    defaultValues: {
      wahaHost: '',
      wahaApiKey: '',
      evolutionHost: '',
      evolutionApiKey: '',
      openaiApiKey: '',
      groqApiKey: '',
    },
  });

  // General settings form
  const {
    register: registerGeneral,
    handleSubmit: handleGeneralSubmit,
    setValue: setGeneralValue,
    formState: { errors: generalErrors, isSubmitting: isGeneralSubmitting },
  } = useForm<GeneralSettingsFormData>({
    resolver: zodResolver(generalSettingsSchema),
  });

  // Helper para fazer requisições autenticadas
  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    return fetch(url, {
      ...options,
      headers,
    });
  };

  // Helper para uploads (sem Content-Type automatico)
  const authenticatedUpload = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    return fetch(url, {
      ...options,
      headers,
    });
  };

  const loadGeneralSettings = async () => {
    try {
      const response = await authenticatedFetch('/api/settings');
      if (response.ok) {
        const data = await response.json();
        setGeneralSettings(data);
        setGeneralValue('companyName', data.companyName || '');
        setGeneralValue('pageTitle', data.pageTitle || '');
      }
    } catch (error) {
      console.error('Erro ao carregar configurações gerais:', error);
      toast.error('Erro ao carregar configurações gerais');
    }
  };

  const onGeneralSubmit = async (data: GeneralSettingsFormData) => {
    try {
      setGeneralSaveMessage(null);
      const response = await authenticatedFetch('/api/settings', {
        method: 'PUT',
        body: JSON.stringify(data),
      });

      if (response.ok) {
        toast.success('Configurações gerais salvas com sucesso');
        setGeneralSaveMessage({ type: 'success', text: 'Informações salvas com sucesso!' });
        loadGeneralSettings();
        // Clear message after 4 seconds
        setTimeout(() => setGeneralSaveMessage(null), 4000);
      } else {
        const errorData = await response.json();
        const errorMsg = errorData.error || 'Erro ao salvar configurações gerais';
        toast.error(errorMsg);
        setGeneralSaveMessage({ type: 'error', text: errorMsg });
      }
    } catch (error) {
      console.error('Erro ao salvar configurações gerais:', error);
      toast.error('Erro ao salvar configurações gerais');
      setGeneralSaveMessage({ type: 'error', text: 'Erro ao salvar configurações gerais' });
    }
  };

  const loadIntegrationSettings = async () => {
    try {
      const response = await authenticatedFetch('/api/settings');
      if (response.ok) {
        const data = await response.json();
        setIntegrationSettings(data);
        setValue('wahaHost', data.wahaHost || '');
        setValue('wahaApiKey', data.wahaApiKey || '');
        setValue('evolutionHost', data.evolutionHost || '');
        setValue('evolutionApiKey', data.evolutionApiKey || '');
        setValue('openaiApiKey', data.openaiApiKey || '');
        setValue('groqApiKey', data.groqApiKey || '');
      }
    } catch (error) {
      console.error('Erro ao carregar configurações de integração:', error);
    }
  };

  const onIntegrationSubmit = async (data: SettingsFormData) => {
    try {
      if (activeModal !== 'waha' && activeModal !== 'evolution') {
        return;
      }

      const integrationData =
        activeModal === 'waha'
          ? { wahaHost: data.wahaHost, wahaApiKey: data.wahaApiKey }
          : { evolutionHost: data.evolutionHost, evolutionApiKey: data.evolutionApiKey };

      console.log('Salvando configurações de integração:', integrationData);
      
      const response = await authenticatedFetch('/api/settings', {
        method: 'PUT',
        body: JSON.stringify(integrationData),
      });

      console.log('Resposta do servidor:', response.status, response.statusText);

      if (response.ok) {
        const responseData = await response.json();
        console.log('Dados salvos com sucesso:', responseData);
        
        toast.success('Configurações de integração salvas com sucesso');
        setActiveModal(null);
        loadIntegrationSettings();
      } else {
        const errorData = await response.json();
        console.error('Erro do servidor:', errorData);
        toast.error(errorData.error || errorData.errors?.[0]?.msg || 'Erro ao salvar configurações de integração');
      }
    } catch (error) {
      console.error('Erro ao salvar configurações de integração:', error);
      toast.error('Erro ao salvar configurações de integração');
    }
  };

  const onOpenAISubmit = async (data: SettingsFormData) => {
    try {
      console.log('Salvando configurações do OpenAI:', data);
      
      // Enviar apenas a API Key do OpenAI
      const openaiData = {
        openaiApiKey: data.openaiApiKey
      };
      
      const response = await authenticatedFetch('/api/settings', {
        method: 'PUT',
        body: JSON.stringify(openaiData),
      });

      console.log('Resposta do servidor:', response.status, response.statusText);

      if (response.ok) {
        const responseData = await response.json();
        console.log('Dados salvos com sucesso:', responseData);
        
        toast.success('Configurações do OpenAI salvas com sucesso');
        setActiveModal(null);
        loadIntegrationSettings();
      } else {
        const errorData = await response.json();
        console.error('Erro do servidor:', errorData);
        toast.error(errorData.error || 'Erro ao salvar configurações do OpenAI');
      }
    } catch (error) {
      console.error('Erro ao salvar configurações do OpenAI:', error);
      toast.error('Erro ao salvar configurações do OpenAI');
    }
  };

  const onGroqSubmit = async (data: SettingsFormData) => {
    try {
      console.log('Salvando configurações do Groq:', data);
      
      // Enviar apenas a API Key do Groq
      const groqData = {
        groqApiKey: data.groqApiKey
      };
      
      const response = await authenticatedFetch('/api/settings', {
        method: 'PUT',
        body: JSON.stringify(groqData),
      });

      console.log('Resposta do servidor:', response.status, response.statusText);

      if (response.ok) {
        const responseData = await response.json();
        console.log('Dados salvos com sucesso:', responseData);
        
        toast.success('Configurações do Groq salvas com sucesso');
        setActiveModal(null);
        loadIntegrationSettings();
      } else {
        const errorData = await response.json();
        console.error('Erro do servidor:', errorData);
        toast.error(errorData.error || 'Erro ao salvar configurações do Groq');
      }
    } catch (error) {
      console.error('Erro ao salvar configurações do Groq:', error);
      toast.error('Erro ao salvar configurações do Groq');
    }
  };

  const removeApiKey = async (keyType: 'openai' | 'groq') => {
    if (!confirm(`Tem certeza que deseja remover a API Key do ${keyType === 'openai' ? 'OpenAI' : 'Groq'}?`)) {
      return;
    }

    try {
      const data: any = {};
      if (keyType === 'openai') {
        data.openaiApiKey = '';
      } else {
        data.groqApiKey = '';
      }

      const response = await authenticatedFetch('/api/settings', {
        method: 'PUT',
        body: JSON.stringify(data),
      });

      if (response.ok) {
        toast.success(`API Key do ${keyType === 'openai' ? 'OpenAI' : 'Groq'} removida com sucesso`);
        setActiveModal(null);
        loadIntegrationSettings();
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao remover API Key');
      }
    } catch (error) {
      console.error('Erro ao remover API Key:', error);
      toast.error('Erro ao remover API Key');
    }
  };

  // Logo handlers
  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadLogo = async () => {
    if (!logoFile) return;

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('logo', logoFile);

      const response = await authenticatedUpload('/api/settings/logo', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        toast.success('Logo carregada com sucesso');
        loadGeneralSettings();
        setLogoFile(null);
        setLogoPreview('');
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao carregar logo');
      }
    } catch (error) {
      console.error('Erro ao carregar logo:', error);
      toast.error('Erro ao carregar logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const removeLogo = async () => {
    try {
      const response = await authenticatedFetch('/api/settings/logo', {
        method: 'DELETE',
      });

      if (response.ok) {
        toast.success('Logo removida com sucesso');
        loadGeneralSettings();
      } else {
        toast.error('Erro ao remover logo');
      }
    } catch (error) {
      console.error('Erro ao remover logo:', error);
      toast.error('Erro ao remover logo');
    }
  };

  // Favicon handlers
  const handleFaviconChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFaviconFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setFaviconPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadFavicon = async () => {
    if (!faviconFile) return;

    setUploadingFavicon(true);
    try {
      const formData = new FormData();
      formData.append('favicon', faviconFile);

      const response = await authenticatedUpload('/api/settings/favicon', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        toast.success('Favicon carregado com sucesso');
        loadGeneralSettings();
        setFaviconFile(null);
        setFaviconPreview('');
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao carregar favicon');
      }
    } catch (error) {
      console.error('Erro ao carregar favicon:', error);
      toast.error('Erro ao carregar favicon');
    } finally {
      setUploadingFavicon(false);
    }
  };

  const removeFavicon = async () => {
    try {
      const response = await authenticatedFetch('/api/settings/favicon', {
        method: 'DELETE',
      });

      if (response.ok) {
        toast.success('Favicon removido com sucesso');
        loadGeneralSettings();
      } else {
        toast.error('Erro ao remover favicon');
      }
    } catch (error) {
      console.error('Erro ao remover favicon:', error);
      toast.error('Erro ao remover favicon');
    }
  };

  // Icon handlers
  const handleIconChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setIconFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setIconPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadIcon = async () => {
    if (!iconFile) return;

    setUploadingIcon(true);
    try {
      const formData = new FormData();
      formData.append('icon', iconFile);

      const response = await authenticatedUpload('/api/settings/icon', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        toast.success('Ícone carregado com sucesso');
        loadGeneralSettings();
        setIconFile(null);
        setIconPreview('');
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao carregar ícone');
      }
    } catch (error) {
      console.error('Erro ao carregar ícone:', error);
      toast.error('Erro ao carregar ícone');
    } finally {
      setUploadingIcon(false);
    }
  };

  const removeIcon = async () => {
    try {
      const response = await authenticatedFetch('/api/settings/icon', {
        method: 'DELETE',
      });

      if (response.ok) {
        toast.success('Ícone removido com sucesso');
        loadGeneralSettings();
      } else {
        toast.error('Erro ao remover ícone');
      }
    } catch (error) {
      console.error('Erro ao remover ícone:', error);
      toast.error('Erro ao remover ícone');
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');

      if (activeTab === 'tenants') {
        const [response, platformOptionsResponse] = await Promise.all([
          fetch('/api/tenants', {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch('/api/superadmin/platforms/options', {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);
        if (response.ok) {
          const data = await response.json();
          setTenants(data.tenants || []);
        }
        if (platformOptionsResponse.ok) {
          const data = await platformOptionsResponse.json();
          setPlatformOptions(data.data || []);
        }
      } else if (activeTab === 'users') {
        // Usar endpoint global para SuperAdmin com paginação e filtros
        const params = new URLSearchParams({
          page: userPage.toString(),
          pageSize: userPageSize.toString(),
          ...(userSearch && { search: userSearch })
        });
        
        const response = await fetch(`/api/users/global?${params}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setUsers(data.data?.users || []);
          setUserTotalPages(data.data?.pagination?.totalPages || 1);
          setUserTotal(data.data?.pagination?.total || 0);
        }
      } else if (activeTab === 'backup') {
        const response = await fetch('/api/backup/stats', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setBackupStats(data.stats || null);
        }
    } else if (activeTab === 'platforms') {
        const response = await fetch('/api/superadmin/platforms', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setPlatforms(data.data || []);
        }
      } else if (activeTab === 'tooltips') {
        const response = await fetch('/api/superadmin/tooltips', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setTooltips(data.data || []);
        } else {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.error || `HTTP ${response.status}`);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // ===== Platforms CRUD handlers =====
  const loadPlatforms = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/superadmin/platforms', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPlatforms(data.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar plataformas:', error);
    }
  };

  const handleCreatePlatform = () => {
    setEditingPlatform(null);
    setPlatformFormData({ name: '', value: '', color: '#3b82f6', icon: '', description: '', url: '', adminToken: '' });
    setShowPlatformToken(false);
    setIsPlatformModalOpen(true);
  };

  const handleEditPlatform = async (platform: Platform) => {
    setEditingPlatform(platform);
    // Buscar token completo
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/superadmin/platforms/${platform.id}?reveal=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const p = data.data;
        setPlatformFormData({
          name: p.name,
          value: p.value,
          color: p.color || '#3b82f6',
          icon: p.icon || '',
          description: p.description || '',
          url: p.url,
          adminToken: p.adminToken
        });
      }
    } catch {
      setPlatformFormData({
        name: platform.name,
        value: platform.value,
        color: platform.color || '#3b82f6',
        icon: platform.icon || '',
        description: platform.description || '',
        url: platform.url,
        adminToken: ''
      });
    }
    setShowPlatformToken(false);
    setIsPlatformModalOpen(true);
  };

  const handleSavePlatform = async () => {
    const { name, value, url, adminToken } = platformFormData;
    if (!name.trim() || !value.trim() || !url.trim()) {
      toast.error('Nome, valor e URL são obrigatórios');
      return;
    }
    if (!editingPlatform && !adminToken.trim()) {
      toast.error('Token Admin é obrigatório para nova plataforma');
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const method = editingPlatform ? 'PUT' : 'POST';
      const endpoint = editingPlatform
        ? `/api/superadmin/platforms/${editingPlatform.id}`
        : '/api/superadmin/platforms';

      const response = await fetch(endpoint, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(platformFormData)
      });

      if (response.ok) {
        toast.success(editingPlatform ? 'Plataforma atualizada!' : 'Plataforma criada!');
        setIsPlatformModalOpen(false);
        loadPlatforms();
      } else {
        const err = await response.json();
        toast.error(err.error || 'Erro ao salvar plataforma');
      }
    } catch (error: any) {
      toast.error(error.message || 'Erro ao salvar plataforma');
    }
  };

  const handleDeletePlatform = async (platform: Platform) => {
    if (!confirm(`Tem certeza que deseja excluir a plataforma "${platform.name}"?`)) return;
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/superadmin/platforms/${platform.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Plataforma excluída!');
        loadPlatforms();
      } else {
        const err = await response.json();
        toast.error(err.error || 'Erro ao excluir plataforma');
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleRevealToken = async (platformId: string) => {
    if (revealedTokens[platformId]) {
      setRevealedTokens(prev => { const next = { ...prev }; delete next[platformId]; return next; });
      return;
    }
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/superadmin/platforms/${platformId}?reveal=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRevealedTokens(prev => ({ ...prev, [platformId]: data.data.adminToken }));
      }
    } catch {
      toast.error('Erro ao revelar token');
    }
  };

  const handleToggleTooltipStatus = async (tooltip: Tooltip) => {
    const token = localStorage.getItem('auth_token');
    try {
      const response = await fetch(`/api/superadmin/tooltips/${tooltip.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ativo: !tooltip.ativo
        })
      });
      if (response.ok) {
        toast.success(`Tooltip ${!tooltip.ativo ? 'ativada' : 'desativada'} com sucesso!`);
        setTooltips(prev => prev.map(t => t.id === tooltip.id ? { ...t, ativo: !t.ativo } : t));
      } else {
        const err = await response.json();
        toast.error(err.error || 'Erro ao alterar status');
      }
    } catch (error: any) {
      toast.error('Erro: ' + error.message);
    }
  };

  const handleSaveTooltip = async (tooltipId: string, updatedFields: Partial<Tooltip>) => {
    const token = localStorage.getItem('auth_token');
    setSavingTooltipId(tooltipId);
    try {
      const response = await fetch(`/api/superadmin/tooltips/${tooltipId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatedFields)
      });
      if (response.ok) {
        toast.success('Alterações salvas com sucesso!');
        const result = await response.json();
        setTooltips(prev => prev.map(t => t.id === tooltipId ? result.data : t));
      } else {
        const err = await response.json();
        toast.error(err.error || 'Erro ao salvar alterações');
      }
    } catch (error: any) {
      toast.error('Erro: ' + error.message);
    } finally {
      setSavingTooltipId(null);
    }
  };

  const handleUploadAttachment = async (tooltipId: string, file: File) => {
    const token = localStorage.getItem('auth_token');
    const formData = new FormData();
    formData.append('anexo', file);

    setUploadingAttachmentId(tooltipId);
    try {
      const response = await fetch('/api/superadmin/tooltips/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      if (response.ok) {
        const result = await response.json();
        // Salva as informações do anexo diretamente no banco de dados
        await handleSaveTooltip(tooltipId, {
          videoAnexoUrl: result.data.url,
          videoAnexoNome: result.data.nome
        });
      } else {
        const err = await response.json();
        toast.error(err.error || 'Erro ao carregar arquivo');
      }
    } catch (error: any) {
      toast.error('Erro ao carregar arquivo: ' + error.message);
    } finally {
      setUploadingAttachmentId(null);
    }
  };

  const handleToggleTenantStatus = async (tenant: Tenant) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/tenants/${tenant.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...tenant,
          active: !tenant.active
        })
      });

      if (response.ok) {
        toast.success(`Empresa ${!tenant.active ? 'ativada' : 'desativada'} com sucesso!`);
        loadData();
      } else {
        throw new Error('Erro ao alterar status da empresa');
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleCreateTenant = () => {
    setEditingTenant(null);
    setFormData({
      name: '',
      platformId: '',
      idPlataforma: '',
      allowWhatsWeb: true,
      allowOfficialApi: true,
      adminUser: {
        nome: '',
        email: '',
        senha: ''
      },
      quotas: {
        maxUsers: '10',
        maxContacts: '1000',
        maxCampaigns: '50',
        maxConnections: '5',
        dispatchLimit: ''
      }
    });
    setIsModalOpen(true);
  };

  const handleEditTenant = (tenant: Tenant) => {
    setEditingTenant(tenant);
    setFormData({
      name: tenant.name,
      platformId: tenant.platformId || tenant.platform?.id || '',
      idPlataforma: tenant.idPlataforma || '',
      allowWhatsWeb: tenant.allowWhatsWeb !== undefined ? tenant.allowWhatsWeb : true,
      allowOfficialApi: tenant.allowOfficialApi !== undefined ? tenant.allowOfficialApi : true,
      adminUser: {
        nome: '',
        email: '',
        senha: ''
      },
      quotas: {
        maxUsers: tenant.quota?.maxUsers?.toString() || '10',
        maxContacts: tenant.quota?.maxContacts?.toString() || '1000',
        maxCampaigns: tenant.quota?.maxCampaigns?.toString() || '50',
        maxConnections: tenant.quota?.maxConnections?.toString() || '5',
        dispatchLimit: tenant.quota?.dispatchLimit?.toString() || ''
      }
    });
    setIsModalOpen(true);
  };

  const handleDeleteTenant = async (tenant: Tenant) => {
    if (!confirm(`Tem certeza que deseja excluir a empresa "${tenant.name}"? Esta ação é irreversível.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/tenants/${tenant.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Tenant excluído com sucesso!');
        loadData();
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Erro ao excluir empresa');
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleSubmitTenant = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || (!editingTenant && (!formData.adminUser.nome || !formData.adminUser.email || !formData.adminUser.senha))) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    const hasPlatform = Boolean(formData.platformId);
    const hasExternalAccountId = Boolean(formData.idPlataforma?.trim());
    if (hasPlatform !== hasExternalAccountId) {
      toast.error('Selecione a plataforma e informe o ID da conta juntos');
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const url = editingTenant ? `/api/tenants/${editingTenant.id}` : '/api/tenants';
      const method = editingTenant ? 'PUT' : 'POST';

      const body: any = {
        name: formData.name,
        quotas: formData.quotas,
        platformId: formData.platformId || null,
        idPlataforma: formData.idPlataforma || null,
        allowWhatsWeb: formData.allowWhatsWeb,
        allowOfficialApi: formData.allowOfficialApi
      };

      if (!editingTenant) {
        body.adminUser = formData.adminUser;
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        toast.success(`Empresa ${editingTenant ? 'atualizada' : 'criada'} com sucesso!`);
        setIsModalOpen(false);
        loadData();
      } else {
        const error = await response.json();
        throw new Error(error.message || `Erro ao ${editingTenant ? 'atualizar' : 'criar'} empresa`);
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const getUsagePercentage = (current: number, max: number) => {
    return Math.round((current / max) * 100);
  };

  const getUsageColor = (percentage: number) => {
    if (percentage >= 90) return 'text-red-600 bg-red-100';
    if (percentage >= 70) return 'text-yellow-600 bg-yellow-100';
    return 'text-green-600 bg-green-100';
  };

  // User management functions
  const loadAvailableTenants = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      console.log('🔍 Loading tenants, token exists:', !!token);

      const response = await fetch('/api/users/tenants', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      console.log('🌐 Response status:', response.status, response.statusText);

      if (response.ok) {
        const data = await response.json();
        console.log('📊 Tenants data:', data);
        setAvailableTenants(data.data?.tenants || []);
        console.log('✅ Available tenants set:', data.data?.tenants?.length || 0);
      } else {
        const errorData = await response.text();
        console.error('❌ Error response:', response.status, errorData);
      }
    } catch (error) {
      console.error('💥 Exception loading tenants:', error);
    }
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setTenantSearchQuery(''); // Limpar busca ao criar novo usuário
    setUserFormData({
      nome: '',
      email: '',
      senha: '',
      role: 'USER',
      tenantId: '',
      tenantIds: [],
      ativo: true
    });
    loadAvailableTenants();
    setIsUserModalOpen(true);
  };

  const handleEditUser = async (user: User) => {
    setEditingUser(user);
    setTenantSearchQuery(''); // Limpar busca ao editar usuário

    // Carregar tenants do usuário via novo endpoint
    let userTenantIds: string[] = [];
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/user-tenants/users/${user.id}/tenants`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('📊 Tenants do usuário:', data);
        userTenantIds = (data.tenants || []).map((t: any) => t.id);
      }
    } catch (err) {
      console.error('Erro ao carregar tenants do usuário:', err);
    }

    setUserFormData({
      nome: user.nome,
      email: user.email,
      senha: '',
      role: user.role as 'SUPERADMIN' | 'ADMIN' | 'USER',
      tenantId: user.tenant?.id || '',
      tenantIds: userTenantIds,
      ativo: user.ativo
    });
    loadAvailableTenants();
    setIsUserModalOpen(true);
  };

  const handleSubmitUser = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!userFormData.nome || !userFormData.email || (!editingUser && !userFormData.senha)) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const url = editingUser ? `/api/users/${editingUser.id}` : '/api/users';
      const method = editingUser ? 'PUT' : 'POST';

      const body: any = {
        nome: userFormData.nome,
        email: userFormData.email,
        role: userFormData.role,
        ativo: userFormData.ativo
      };

      // Only send password if it's provided
      if (userFormData.senha) {
        body.senha = userFormData.senha;
      }

      // Handle tenant assignment based on role
      // Para SUPERADMIN, não enviamos tenantId (será null no backend)
      // Para outros roles, enviamos o primeiro tenant selecionado como compatibilidade
      if (userFormData.role !== 'SUPERADMIN' && userFormData.tenantIds && userFormData.tenantIds.length > 0) {
        body.tenantId = userFormData.tenantIds[0];
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        const result = await response.json();
        const userId = editingUser ? editingUser.id : result.data?.user?.id;

        // Se tem tenants selecionados, criar associações (para qualquer role, incluindo SUPERADMIN)
        if (userFormData.tenantIds && userFormData.tenantIds.length > 0 && userId) {
          // Se estamos editando, primeiro remover associações antigas
          if (editingUser) {
            try {
              // Buscar associações existentes
              const existingAssociationsResponse = await fetch(`/api/user-tenants`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });

              if (existingAssociationsResponse.ok) {
                const existingData = await existingAssociationsResponse.json();
                // Remover associações que não estão mais selecionadas
                for (const association of existingData.tenants || []) {
                  if (!userFormData.tenantIds.includes(association.id)) {
                    try {
                      await fetch(`/api/user-tenants/associations/${userId}/${association.id}`, {
                        method: 'DELETE',
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                    } catch (err) {
                      console.warn('Erro ao remover associação:', association.id, err);
                    }
                  }
                }
              }
            } catch (err) {
              console.warn('Erro ao buscar/remover associações antigas:', err);
            }
          }

          // Criar associações para todos os tenants selecionados
          for (const tenantId of userFormData.tenantIds) {
            try {
              const associationResponse = await fetch('/api/user-tenants/associations', {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  userId,
                  tenantId,
                  role: userFormData.role
                })
              });

              // Se der erro 400 (já existe), não é problema
              if (!associationResponse.ok && associationResponse.status !== 400) {
                console.warn('Erro ao criar associação para tenant:', tenantId);
              }
            } catch (err) {
              console.warn('Erro ao criar associação para tenant:', tenantId, err);
            }
          }
        }

        toast.success(`Usuário ${editingUser ? 'atualizado' : 'criado'} com sucesso!`);
        setIsUserModalOpen(false);
        loadData();
      } else {
        const error = await response.json();

        // Verificar se é erro de quota
        if (error.upgradeRequired || (error.message && error.message.includes('Limite'))) {
          toast.error(error.message || 'Limite de usuários atingido. Faça upgrade do plano para continuar.', {
            duration: 6000,
            icon: '⚠️'
          });
          return;
        }

        throw new Error(error.message || `Erro ao ${editingUser ? 'atualizar' : 'criar'} usuário`);
      }
    } catch (error: any) {
      // Não mostrar toast de erro genérico se já mostramos o toast específico de quota
      if (!(error.message && error.message.includes('Limite'))) {
        toast.error(error.message);
      }
    }
  };

  const handleToggleUserStatus = async (user: User) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ativo: !user.ativo
        })
      });

      if (response.ok) {
        toast.success(`Usuário ${!user.ativo ? 'ativado' : 'desativado'} com sucesso!`);
        loadData();
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Erro ao alterar status do usuário');
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleDeleteUser = async (user: User) => {
    if (!confirm(`Tem certeza que deseja excluir o usuário ${user.nome}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/users/${user.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Usuário excluído com sucesso!');
        loadData();
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Erro ao excluir usuário');
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded mb-4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <SuperAdminNav />
      {/* MODIFICADO: Container principal - adaptado para tema dark */}
      {/* ANTES: bg-gray-50 dark:bg-gray-900 */}
      <div 
        className="p-8 min-h-screen"
        style={{ backgroundColor: 'var(--bg-primary)' }}
      >
        {!isUrlLogin && (
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 
              className="text-3xl font-bold"
              style={{ color: 'var(--text-primary)' }}
            >
              Super Admin
            </h1>
            <p 
              className="mt-2"
              style={{ color: 'var(--text-secondary)' }}
            >
              Gerenciamento completo do sistema
            </p>
          </div>
        </div>
        )}

      {/* MODIFICADO: Empresas Tab - adaptado para tema dark */}
      {/* ANTES: bg-white, text-gray-900, border-gray-200, divide-gray-200, etc. */}
      {activeTab === 'tenants' && (
        <div 
          className="rounded-lg shadow"
          style={{
            backgroundColor: 'var(--bg-card)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-color)'
          }}
        >
          <div 
            className="px-6 py-4 border-b flex items-center justify-between"
            style={{ borderColor: 'var(--border-color)' }}
          >
            <h2 
              className="text-lg font-semibold"
              style={{ color: 'var(--text-primary)' }}
            >
              Gerenciar Empresas
            </h2>
            <button
              onClick={handleCreateTenant}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Criar Empresa
            </button>
          </div>

          {/* Filtros */}
          <div 
            className="px-6 py-4 border-b"
            style={{ borderColor: 'var(--border-color)' }}
          >
            <div className="flex items-center gap-3 flex-wrap">
              {/* Busca por nome */}
              <div className="relative flex-1 min-w-[200px]">
                <svg 
                  className="absolute left-3 top-2.5 h-4 w-4" 
                  style={{ color: 'var(--text-secondary)' }}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Buscar por nome..."
                  value={tenantSearchName}
                  onChange={(e) => setTenantSearchName(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  style={{ 
                    backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#f9fafb',
                    color: 'var(--text-primary)', 
                    border: '1px solid var(--border-color)' 
                  }}
                />
              </div>

              {/* Dropdown de plataforma */}
              <select
                value={tenantFilterPlatform}
                onChange={(e) => setTenantFilterPlatform(e.target.value)}
                className="px-3 py-2 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none min-w-[200px]"
                style={{ 
                  backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#f9fafb',
                  color: 'var(--text-primary)', 
                  border: '1px solid var(--border-color)',
                  colorScheme: theme === 'dark' ? 'dark' : 'light'
                }}
              >
                <option value="" style={{ backgroundColor: theme === 'dark' ? '#1f1f23' : '#ffffff', color: theme === 'dark' ? '#e4e4e7' : '#1c1c1e' }}>Todas as plataformas</option>
                {uniquePlatforms.map(p => (
                  <option key={p} value={p} style={{ backgroundColor: theme === 'dark' ? '#1f1f23' : '#ffffff', color: theme === 'dark' ? '#e4e4e7' : '#1c1c1e' }}>{p}</option>
                ))}
              </select>

              {/* Busca por ID da plataforma */}
              <input
                type="text"
                placeholder="Buscar por ID..."
                value={tenantSearchPlatformId}
                onChange={(e) => setTenantSearchPlatformId(e.target.value)}
                className="px-3 py-2 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none w-[140px]"
                style={{ 
                  backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#f9fafb',
                  color: 'var(--text-primary)', 
                  border: '1px solid var(--border-color)' 
                }}
              />

              {/* Limpar filtros + contador */}
              <div className="flex items-center gap-2">
                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                  {filteredTenants.length}/{tenants.length} empresa{tenants.length !== 1 ? 's' : ''}
                </span>
                {hasActiveTenantFilters && (
                  <button
                    onClick={clearTenantFilters}
                    className="px-3 py-2 rounded-lg text-xs font-medium transition-colors"
                    style={{ 
                      backgroundColor: theme === 'dark' ? 'rgba(239, 68, 68, 0.1)' : '#fef2f2',
                      color: theme === 'dark' ? '#fca5a5' : '#dc2626',
                      border: '1px solid ' + (theme === 'dark' ? 'rgba(239, 68, 68, 0.2)' : '#fecaca')
                    }}
                  >
                    ✕ Limpar
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
            {filteredTenants.map((tenant) => (
              <div 
                key={tenant.id} 
                className="p-6 transition-colors"
                style={{ borderColor: 'var(--border-color)' }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-4">
                      <div>
                        <h3 
                          className="text-lg font-semibold"
                          style={{ color: 'var(--text-primary)' }}
                        >
                          {tenant.name}
                        </h3>
                        <p 
                          className="text-sm"
                          style={{ color: 'var(--text-secondary)' }}
                        >
                          <span className="font-mono">{tenant.slug}</span>
                        </p>
                      </div>

                      <span 
                        className="inline-flex px-2 py-1 text-xs font-semibold rounded-full"
                        style={{
                          backgroundColor: tenant.active
                            ? (theme === 'dark' ? '#166534' : '#dcfce7')
                            : (theme === 'dark' ? '#991b1b' : '#fee2e2'),
                          color: tenant.active
                            ? (theme === 'dark' ? '#86efac' : '#166534')
                            : (theme === 'dark' ? '#fca5a5' : '#991b1b')
                        }}
                      >
                        {tenant.active ? 'Ativo' : 'Inativo'}
                      </span>

                      {/* Plataforma e ID */}
                      {tenant.plataforma && (
                        <div className="flex items-center gap-2">
                          <span 
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full"
                            style={{
                              backgroundColor: theme === 'dark' ? 'rgba(59, 130, 246, 0.15)' : '#eff6ff',
                              color: theme === 'dark' ? '#93c5fd' : '#1d4ed8'
                            }}
                          >
                            🌐 {tenant.plataforma}
                          </span>
                          {tenant.idPlataforma && (
                            <span 
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-mono font-medium rounded-full"
                              style={{
                                backgroundColor: theme === 'dark' ? 'rgba(139, 92, 246, 0.15)' : '#f5f3ff',
                                color: theme === 'dark' ? '#c4b5fd' : '#6d28d9'
                              }}
                            >
                              ID: {tenant.idPlataforma}
                            </span>
                          )}
                        </div>
                      )}

                      {/* WhatsApp permissions */}
                      {(tenant.allowWhatsWeb || tenant.allowOfficialApi) && (
                        <div className="flex items-center gap-1.5">
                          {tenant.allowWhatsWeb && (
                            <span 
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full"
                              style={{
                                backgroundColor: theme === 'dark' ? 'rgba(34, 197, 94, 0.12)' : '#f0fdf4',
                                color: theme === 'dark' ? '#86efac' : '#15803d'
                              }}
                            >
                              📱 WhatsApp Web
                            </span>
                          )}
                          {tenant.allowOfficialApi && (
                            <span 
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full"
                              style={{
                                backgroundColor: theme === 'dark' ? 'rgba(37, 211, 102, 0.12)' : '#ecfdf5',
                                color: theme === 'dark' ? '#4ade80' : '#047857'
                              }}
                            >
                              ☁️ API Oficial
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* MODIFICADO: Usage Stats - adaptado para tema dark */}
                    {/* ANTES: text-gray-500, getUsageColor com cores fixas */}
                    {tenant._count && tenant.quota && (
                      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[
                          { label: 'Usuários', current: tenant._count.users, max: tenant.quota.maxUsers },
                          { label: 'Contatos', current: tenant._count.contacts, max: tenant.quota.maxContacts },
                          { label: 'Campanhas', current: tenant._count.campaigns, max: tenant.quota.maxCampaigns },
                          { label: 'Conexões', current: tenant._count.whatsappSessions, max: tenant.quota.maxConnections }
                        ].map((stat) => {
                          const percentage = getUsagePercentage(stat.current, stat.max);
                          const usageColor = percentage < 50 
                            ? (theme === 'dark' ? '#166534' : '#dcfce7')
                            : percentage < 80
                            ? (theme === 'dark' ? '#854d0e' : '#fef3c7')
                            : (theme === 'dark' ? '#991b1b' : '#fee2e2');
                          const textColor = percentage < 50
                            ? (theme === 'dark' ? '#86efac' : '#166534')
                            : percentage < 80
                            ? (theme === 'dark' ? '#fbbf24' : '#92400e')
                            : (theme === 'dark' ? '#fca5a5' : '#991b1b');
                          return (
                            <div key={stat.label} className="text-center">
                              <div 
                                className="text-xs px-2 py-1 rounded-full"
                                style={{
                                  backgroundColor: usageColor,
                                  color: textColor
                                }}
                              >
                                {stat.current}/{stat.max} ({percentage}%)
                              </div>
                              <p 
                                className="text-xs mt-1"
                                style={{ color: 'var(--text-secondary)' }}
                              >
                                {stat.label}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-1 ml-4">
                    {/* Edit icon button */}
                    <button
                      onClick={() => handleEditTenant(tenant)}
                      className="inline-flex items-center justify-center h-6 w-6 p-0 rounded-lg transition-all duration-200 ease-in-out focus:outline-none"
                      title="Editar"
                      aria-label={`Editar empresa ${tenant.name}`}
                      style={{
                        background: (theme === 'dark' ? 'rgba(105, 110, 119, 0.1)' : 'rgba(139, 141, 152, 0.1)'),
                        color: (theme === 'dark' ? '#EDEEF0' : '#1C2024') // Branco no dark, escuro no light
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = (theme === 'dark' ? 'rgba(105, 110, 119, 0.2)' : 'rgba(139, 141, 152, 0.2)');
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = (theme === 'dark' ? 'rgba(105, 110, 119, 0.1)' : 'rgba(139, 141, 152, 0.1)');
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden="true">
                        <path d="M12 20h9" />
                        <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4Z" />
                      </svg>
                    </button>
                    {/* Toggle status icon button */}
                    <button
                      onClick={() => handleToggleTenantStatus(tenant)}
                      className="inline-flex items-center justify-center h-6 w-6 p-0 rounded-lg transition-all duration-200 ease-in-out focus:outline-none"
                      title={tenant.active ? 'Desativar' : 'Ativar'}
                      aria-label={`${tenant.active ? 'Desativar' : 'Ativar'} empresa ${tenant.name}`}
                      style={{
                        background: tenant.active ? 'rgba(229, 70, 102, 0.15)' : 'rgba(16, 185, 129, 0.15)',
                        color: tenant.active ? '#FF949D' : '#86EFAC' // Ícones claros para bom contraste
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = tenant.active ? 'rgba(229, 70, 102, 0.2)' : 'rgba(16, 185, 129, 0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = tenant.active ? 'rgba(229, 70, 102, 0.15)' : 'rgba(16, 185, 129, 0.15)';
                      }}
                    >
                      {tenant.active ? (
                        // Lucide "ban" icon
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="h-4 w-4"
                          aria-hidden="true"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <path d="m4.9 4.9 14.2 14.2" />
                        </svg>
                      ) : (
                        // Lucide "check" icon
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="h-4 w-4"
                          aria-hidden="true"
                        >
                          <path d="M20 6 9 17l-5-5" />
                        </svg>
                      )}
                    </button>
                    {/* Delete icon button */}
                    <button
                      onClick={() => handleDeleteTenant(tenant)}
                      className="inline-flex items-center justify-center h-6 w-6 p-0 rounded-lg transition-all duration-200 ease-in-out focus:outline-none"
                      title="Excluir"
                      aria-label={`Excluir empresa ${tenant.name}`}
                      style={{
                        background: 'rgba(229, 70, 102, 0.15)',
                        color: '#FF949D' // Ícone rosa claro para bom contraste
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(229, 70, 102, 0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(229, 70, 102, 0.15)';
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden="true">
                        <path d="M3 6h18" />
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                        <path d="M10 11v6" />
                        <path d="M14 11v6" />
                        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {filteredTenants.length === 0 && (
              <div className="p-8 text-center">
                <svg 
                  className="mx-auto h-12 w-12" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                  style={{ color: 'var(--text-muted)' }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h3M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 8h1m-1-4h1m4 4h1m-1-4h1" />
                </svg>
                <p 
                  className="mt-2 text-sm"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  {hasActiveTenantFilters ? 'Nenhuma empresa encontrada com os filtros aplicados' : 'Nenhuma empresa encontrada'}
                </p>
                {hasActiveTenantFilters && (
                  <button
                    onClick={clearTenantFilters}
                    className="mt-3 px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                  >
                    Limpar filtros
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Platforms Tab */}
      {activeTab === 'platforms' && (
        <div
          className="rounded-lg shadow"
          style={{
            backgroundColor: 'var(--bg-card)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-color)'
          }}
        >
          <div
            className="px-6 py-4 border-b flex items-center justify-between"
            style={{ borderColor: 'var(--border-color)' }}
          >
            <div>
              <h2
                className="text-lg font-semibold"
                style={{ color: 'var(--text-primary)' }}
              >
                Gerenciar Plataformas
              </h2>
              <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                {platforms.length > 0
                  ? `${platforms.length} plataforma${platforms.length !== 1 ? 's' : ''} cadastrada${platforms.length !== 1 ? 's' : ''}`
                  : 'Cadastre as plataformas permitidas e seus tokens de administração'}
              </p>
            </div>
            <button
              onClick={handleCreatePlatform}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nova Plataforma
            </button>
          </div>

          <div className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
            {platforms.map(platform => (
              <div
                key={platform.id}
                className="px-6 py-4 flex items-center justify-between hover:bg-opacity-50 transition-colors"
                style={{ borderColor: 'var(--border-color)' }}
              >
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  {/* Cor da plataforma */}
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: platform.color || '#3b82f6' }}
                  />

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium" style={{ color: 'var(--text-primary)' }}>
                        {platform.name}
                      </span>
                      <span
                        className="text-xs px-2 py-0.5 rounded-full"
                        style={{
                          backgroundColor: theme === 'dark' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)',
                          color: '#3b82f6'
                        }}
                      >
                        {platform.value}
                      </span>
                      {!platform.active && (
                        <span
                          className="text-xs px-2 py-0.5 rounded-full"
                          style={{
                            backgroundColor: theme === 'dark' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(239, 68, 68, 0.1)',
                            color: '#ef4444'
                          }}
                        >
                          Inativa
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-xs truncate" style={{ color: 'var(--text-muted)', maxWidth: '300px' }}>
                        🔗 {platform.url}
                      </span>
                      {platform.description && (
                        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                          · {platform.description}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Token mascarado + botão olho */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <code
                      className="text-xs px-2 py-1 rounded"
                      style={{
                        backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                        color: 'var(--text-secondary)',
                        fontFamily: 'monospace'
                      }}
                    >
                      {revealedTokens[platform.id] || platform.adminToken}
                    </code>
                    <button
                      onClick={() => handleRevealToken(platform.id)}
                      className="p-1 rounded transition-colors"
                      style={{ color: 'var(--text-muted)' }}
                      title={revealedTokens[platform.id] ? 'Ocultar token' : 'Revelar token'}
                    >
                      {revealedTokens[platform.id] ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>

                {/* Ações */}
                <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                  <button
                    onClick={() => handleEditPlatform(platform)}
                    className="inline-flex items-center justify-center h-7 w-7 rounded-lg transition-all"
                    title="Editar"
                    style={{
                      background: 'rgba(59, 130, 246, 0.15)',
                      color: '#60a5fa'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(59, 130, 246, 0.25)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(59, 130, 246, 0.15)'; }}
                  >
                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDeletePlatform(platform)}
                    className="inline-flex items-center justify-center h-7 w-7 rounded-lg transition-all"
                    title="Excluir"
                    style={{
                      background: 'rgba(229, 70, 102, 0.15)',
                      color: '#FF949D'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(229, 70, 102, 0.25)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(229, 70, 102, 0.15)'; }}
                  >
                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6h18M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6M10 11v6M14 11v6M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}

            {platforms.length === 0 && (
              <div className="p-12 text-center">
                <svg
                  className="mx-auto h-12 w-12"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  style={{ color: 'var(--text-muted)' }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
                <p className="mt-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Nenhuma plataforma cadastrada
                </p>
                <p className="mt-1 text-xs" style={{ color: 'var(--text-muted)' }}>
                  Cadastre as plataformas permitidas e seus tokens de administração
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Platform Modal */}
      {isPlatformModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div
            className="rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            style={{
              backgroundColor: 'var(--bg-card)',
              border: '1px solid var(--border-color)'
            }}
          >
            <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--border-color)' }}>
              <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                {editingPlatform ? 'Editar Plataforma' : 'Nova Plataforma'}
              </h3>
              <button
                onClick={() => setIsPlatformModalOpen(false)}
                className="p-1 rounded-lg transition-colors"
                style={{ color: 'var(--text-muted)' }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="px-6 py-4 space-y-4">
              {/* Nome */}
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                  Nome *
                </label>
                <input
                  type="text"
                  value={platformFormData.name}
                  onChange={e => setPlatformFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: Ativo, Nican"
                  className="w-full px-3 py-2 rounded-lg text-sm transition-colors"
                  style={{
                    backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                    border: '1px solid var(--border-color)',
                    color: 'var(--text-primary)',
                    outline: 'none'
                  }}
                />
              </div>

              {/* Valor (slug) */}
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                  Valor (slug) *
                </label>
                <input
                  type="text"
                  value={platformFormData.value}
                  onChange={e => setPlatformFormData(prev => ({ ...prev, value: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') }))}
                  placeholder="Ex: ativo, nican"
                  className="w-full px-3 py-2 rounded-lg text-sm transition-colors"
                  style={{
                    backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                    border: '1px solid var(--border-color)',
                    color: 'var(--text-primary)',
                    outline: 'none'
                  }}
                />
                <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                  Apenas letras minúsculas, números, hifens e underscores
                </p>
              </div>

              {/* URL */}
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                  URL da Plataforma *
                </label>
                <input
                  type="url"
                  value={platformFormData.url}
                  onChange={e => setPlatformFormData(prev => ({ ...prev, url: e.target.value }))}
                  placeholder="https://app.minhaPlataforma.com"
                  className="w-full px-3 py-2 rounded-lg text-sm transition-colors"
                  style={{
                    backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                    border: '1px solid var(--border-color)',
                    color: 'var(--text-primary)',
                    outline: 'none'
                  }}
                />
              </div>

              {/* Token Admin com olho */}
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                  Token Admin {!editingPlatform && '*'}
                </label>
                <div className="relative">
                  <input
                    type={showPlatformToken ? 'text' : 'password'}
                    value={platformFormData.adminToken}
                    onChange={e => setPlatformFormData(prev => ({ ...prev, adminToken: e.target.value }))}
                    placeholder={editingPlatform ? 'Deixe vazio para manter o atual' : 'Token de administração'}
                    className="w-full px-3 py-2 pr-10 rounded-lg text-sm transition-colors"
                    style={{
                      backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                      border: '1px solid var(--border-color)',
                      color: 'var(--text-primary)',
                      outline: 'none'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPlatformToken(!showPlatformToken)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded transition-colors"
                    style={{ color: 'var(--text-muted)' }}
                    title={showPlatformToken ? 'Ocultar token' : 'Mostrar token'}
                  >
                    {showPlatformToken ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                  Token com permissões de admin para integrações
                </p>
              </div>

              {/* Cor + Ícone */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                    Cor
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={platformFormData.color}
                      onChange={e => setPlatformFormData(prev => ({ ...prev, color: e.target.value }))}
                      className="w-8 h-8 rounded cursor-pointer border-0"
                      style={{ backgroundColor: 'transparent' }}
                    />
                    <input
                      type="text"
                      value={platformFormData.color}
                      onChange={e => setPlatformFormData(prev => ({ ...prev, color: e.target.value }))}
                      className="flex-1 px-3 py-2 rounded-lg text-sm"
                      style={{
                        backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                        border: '1px solid var(--border-color)',
                        color: 'var(--text-primary)',
                        outline: 'none'
                      }}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                    Ícone (Lucide)
                  </label>
                  <input
                    type="text"
                    value={platformFormData.icon}
                    onChange={e => setPlatformFormData(prev => ({ ...prev, icon: e.target.value }))}
                    placeholder="Ex: globe, check-circle"
                    className="w-full px-3 py-2 rounded-lg text-sm"
                    style={{
                      backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                      border: '1px solid var(--border-color)',
                      color: 'var(--text-primary)',
                      outline: 'none'
                    }}
                  />
                </div>
              </div>

              {/* Descrição */}
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                  Descrição
                </label>
                <input
                  type="text"
                  value={platformFormData.description}
                  onChange={e => setPlatformFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descrição opcional da plataforma"
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{
                    backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                    border: '1px solid var(--border-color)',
                    color: 'var(--text-primary)',
                    outline: 'none'
                  }}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t flex justify-end gap-3" style={{ borderColor: 'var(--border-color)' }}>
              <button
                onClick={() => setIsPlatformModalOpen(false)}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                style={{
                  backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)',
                  color: 'var(--text-secondary)'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleSavePlatform}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors"
              >
                {editingPlatform ? 'Salvar Alterações' : 'Criar Plataforma'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tooltips Tab */}
      {activeTab === 'tooltips' && (
        <div
          className="rounded-lg shadow animate-fade-in"
          style={{
            backgroundColor: 'var(--bg-card)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-color)'
          }}
        >
          <div
            className="px-6 py-4 border-b flex items-center justify-between"
            style={{ borderColor: 'var(--border-color)' }}
          >
            <div>
              <h2
                className="text-lg font-semibold"
                style={{ color: 'var(--text-primary)' }}
              >
                Gerenciar Tooltips (Tutoriais)
              </h2>
              <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                Configure as dicas e videoaulas de ajuda exibidas na plataforma
              </p>
            </div>
          </div>

          {/* Filtros e Busca */}
          <div
            className="px-6 py-4 border-b"
            style={{ borderColor: 'var(--border-color)' }}
          >
            <div className="flex items-center gap-3 flex-wrap">
              {/* Busca */}
              <div className="relative flex-1 min-w-[250px]">
                <svg
                  className="absolute left-3 top-2.5 h-4 w-4"
                  style={{ color: 'var(--text-secondary)' }}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Buscar por título, identificador ou mensagem..."
                  value={tooltipSearch}
                  onChange={(e) => setTooltipSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  style={{
                    backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#f9fafb',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-color)'
                  }}
                />
              </div>

              {/* Setores/Módulos */}
              <select
                value={tooltipFilterSector}
                onChange={(e) => setTooltipFilterSector(e.target.value)}
                className="px-3 py-2 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none min-w-[160px]"
                style={{
                  backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#f9fafb',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-color)',
                  colorScheme: theme === 'dark' ? 'dark' : 'light'
                }}
              >
                <option value="">Todos os setores</option>
                {Array.from(new Set(tooltips.map(t => t.localizacao.split(' > ')[0]).filter(Boolean))).map(sector => (
                  <option key={sector} value={sector}>{sector}</option>
                ))}
              </select>

              {/* Status */}
              <select
                value={tooltipFilterStatus}
                onChange={(e) => setTooltipFilterStatus(e.target.value as any)}
                className="px-3 py-2 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none min-w-[130px]"
                style={{
                  backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#f9fafb',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-color)',
                  colorScheme: theme === 'dark' ? 'dark' : 'light'
                }}
              >
                <option value="all">Todos status</option>
                <option value="active">Ativas</option>
                <option value="inactive">Inativas</option>
              </select>
            </div>
          </div>

          {/* Listagem Accordion */}
          <div className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
            {tooltips
              .filter(t => {
                const search = tooltipSearch.toLowerCase();
                const matchesSearch = t.titulo.toLowerCase().includes(search) || 
                                      t.identificador.toLowerCase().includes(search) ||
                                      t.mensagem.toLowerCase().includes(search);
                const sector = t.localizacao.split(' > ')[0];
                const matchesSector = !tooltipFilterSector || sector === tooltipFilterSector;
                const matchesStatus = tooltipFilterStatus === 'all' || 
                                     (tooltipFilterStatus === 'active' && t.ativo) ||
                                     (tooltipFilterStatus === 'inactive' && !t.ativo);
                return matchesSearch && matchesSector && matchesStatus;
              })
              .map(t => {
                const isExpanded = expandedTooltipId === t.id;
                return (
                  <div key={t.id} className="transition-all">
                    {/* Header do Accordion */}
                    <div
                      onClick={() => setExpandedTooltipId(isExpanded ? null : t.id)}
                      className="px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <span className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
                          {t.titulo}
                        </span>
                        <span
                          className="text-xs px-2 py-0.5 rounded-full font-mono"
                          style={{
                            backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
                            color: 'var(--text-secondary)'
                          }}
                        >
                          {t.identificador}
                        </span>
                        <span
                          className="text-xs px-2 py-0.5 rounded-full font-medium"
                          style={{
                            backgroundColor: theme === 'dark' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)',
                            color: '#3b82f6'
                          }}
                        >
                          📍 {t.localizacao}
                        </span>
                      </div>

                      <div className="flex items-center gap-4 ml-4" onClick={e => e.stopPropagation()}>
                        {/* Status Toggle Switch */}
                        <div className="flex items-center gap-2">
                          <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                            {t.ativo ? 'Ativa' : 'Inativa'}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleToggleTooltipStatus(t)}
                            className="relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none"
                            style={{
                              backgroundColor: t.ativo ? '#10b981' : (theme === 'dark' ? '#3f3f46' : '#d1d5db')
                            }}
                          >
                            <span
                              className="inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform"
                              style={{
                                transform: t.ativo ? 'translateX(18px)' : 'translateX(3px)'
                              }}
                            />
                          </button>
                        </div>

                        {/* Chevron */}
                        <button
                          type="button"
                          onClick={() => setExpandedTooltipId(isExpanded ? null : t.id)}
                          className="p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                        >
                          <svg
                            className={`w-4 h-4 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                            fill="none" stroke="currentColor" viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Corpo do Accordion (Formulário) */}
                    {isExpanded && (
                      <TooltipForm
                        tooltip={t}
                        theme={theme}
                        onSave={(fields) => handleSaveTooltip(t.id, fields)}
                        onUploadAttachment={(file) => handleUploadAttachment(t.id, file)}
                        onRemoveAttachment={() => handleSaveTooltip(t.id, { videoAnexoUrl: null, videoAnexoNome: null })}
                        saving={savingTooltipId === t.id}
                        uploading={uploadingAttachmentId === t.id}
                      />
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="rounded-lg shadow" style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}>
          <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Gerenciar Usuários</h2>
                <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                  {userTotal > 0 ? `${userTotal} usuário${userTotal !== 1 ? 's' : ''} encontrado${userTotal !== 1 ? 's' : ''}` : 'Visualização e gerenciamento global de todos os usuários do sistema'}
                </p>
              </div>
              <button
                onClick={handleCreateUser}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Adicionar Usuário
              </button>
            </div>
            
            {/* Campo de busca */}
            <div className="relative">
              <input
                type="text"
                placeholder="Buscar por nome, email ou empresa..."
                value={userSearch}
                onChange={(e) => {
                  setUserSearch(e.target.value);
                  setUserPage(1); // Reset para primeira página ao buscar
                }}
                className="w-full pl-10 pr-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500"
                style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
              />
              <svg
                className="absolute left-3 top-2.5 h-5 w-5"
                style={{ color: 'var(--text-secondary)' }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              {userSearch && (
                <button
                  onClick={() => {
                    setUserSearch('');
                    setUserPage(1);
                  }}
                  className="absolute right-3 top-2.5"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>
          
          {/* Tabela com scroll */}
          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="min-w-full divide-y divide-[var(--border-color-soft)] transition-colors">
              <thead style={{ backgroundColor: 'var(--bg-secondary)' }}>
                <tr>
                  {['Usuário','Empresa','Role','Status','Último Login','Criado em','Ações'].map((h, i) => (
                    <th key={i} className={`px-6 py-3 ${i===6 ? 'text-right' : 'text-left'} text-sm font-semibold uppercase tracking-wider`} style={{ color: 'var(--text-secondary)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-color-soft)] transition-colors">
                {users.map((user) => (
                  <tr key={user.id} className="transition-colors" style={{ backgroundColor: 'transparent' }} onMouseEnter={(e) => { (e.currentTarget as HTMLTableRowElement).style.backgroundColor = 'var(--bg-secondary)'; }} onMouseLeave={(e) => { (e.currentTarget as HTMLTableRowElement).style.backgroundColor = 'transparent'; }}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{user.nome}</div>
                        <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>{user.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.tenant ? (
                        <div>
                          <div className="text-sm" style={{ color: 'var(--text-primary)' }}>{user.tenant.name}</div>
                          <div className="text-xs font-mono" style={{ color: 'var(--text-secondary)' }}>{user.tenant.slug}</div>
                        </div>
                      ) : (
                        <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Sistema</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        user.role === 'SUPERADMIN'
                          ? 'bg-purple-100 text-purple-800'
                          : user.role === 'ADMIN'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {user.role === 'SUPERADMIN' ? 'Super Admin' :
                         user.role === 'ADMIN' ? 'Admin' : 'Usuário'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        user.ativo
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {user.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
                      {user.ultimoLogin
                        ? new Date(user.ultimoLogin).toLocaleDateString('pt-BR')
                        : 'Nunca'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
                      {new Date(user.criadoEm).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex justify-end gap-1">
                        {/* Edit icon button */}
                        <button
                          onClick={() => handleEditUser(user)}
                          className="inline-flex items-center justify-center h-6 w-6 p-0 rounded-lg transition-all duration-200 ease-in-out focus:outline-none"
                          title="Editar"
                          aria-label={`Editar usuário ${user.nome}`}
                          style={{
                            background: (theme === 'dark' ? 'rgba(105, 110, 119, 0.1)' : 'rgba(139, 141, 152, 0.1)'),
                            color: (theme === 'dark' ? '#EDEEF0' : '#1C2024') // Branco no dark, escuro no light
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = (theme === 'dark' ? 'rgba(105, 110, 119, 0.2)' : 'rgba(139, 141, 152, 0.2)');
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = (theme === 'dark' ? 'rgba(105, 110, 119, 0.1)' : 'rgba(139, 141, 152, 0.1)');
                          }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden="true">
                            <path d="M12 20h9" />
                            <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4Z" />
                          </svg>
                        </button>
                        {/* Toggle status stays as text for clarity */}
                        <button
                          onClick={() => handleToggleUserStatus(user)}
                          className="inline-flex items-center justify-center h-6 w-6 p-0 rounded-lg transition-all duration-200 ease-in-out focus:outline-none mx-1"
                          title={user.ativo ? 'Desativar' : 'Ativar'}
                          aria-label={`${user.ativo ? 'Desativar' : 'Ativar'} usuário ${user.nome}`}
                          style={{
                            background: user.ativo ? 'rgba(229, 70, 102, 0.15)' : 'rgba(16, 185, 129, 0.15)',
                            color: user.ativo ? '#FF949D' : '#86EFAC' // Ícones claros para bom contraste
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = user.ativo ? 'rgba(229, 70, 102, 0.2)' : 'rgba(16, 185, 129, 0.2)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = user.ativo ? 'rgba(229, 70, 102, 0.15)' : 'rgba(16, 185, 129, 0.15)';
                          }}
                        >
                          {user.ativo ? (
                            // Lucide "ban" icon
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="h-4 w-4"
                              aria-hidden="true"
                            >
                              <circle cx="12" cy="12" r="10" />
                              <path d="m4.9 4.9 14.2 14.2" />
                            </svg>
                          ) : (
                            // Lucide "check" icon
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="h-4 w-4"
                              aria-hidden="true"
                            >
                              <path d="M20 6 9 17l-5-5" />
                            </svg>
                          )}
                        </button>
                        {/* Delete icon button */}
                        {user.role !== 'SUPERADMIN' && (
                          <button
                            onClick={() => handleDeleteUser(user)}
                            className="inline-flex items-center justify-center h-6 w-6 p-0 rounded-lg transition-all duration-200 ease-in-out focus:outline-none"
                            title="Excluir"
                            aria-label={`Excluir usuário ${user.nome}`}
                            style={{
                              background: 'rgba(229, 70, 102, 0.15)',
                              color: '#FF949D' // Ícone rosa claro para bom contraste
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'rgba(229, 70, 102, 0.2)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'rgba(229, 70, 102, 0.15)';
                            }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden="true">
                              <path d="M3 6h18" />
                              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                              <path d="M10 11v6" />
                              <path d="M14 11v6" />
                              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {users.length === 0 && !loading && (
              <div className="p-8 text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <p className="mt-2 text-sm text-gray-500">
                  {userSearch ? 'Nenhum usuário encontrado com os filtros aplicados' : 'Nenhum usuário encontrado'}
                </p>
              </div>
            )}
          </div>
          
          {/* Paginação */}
          {userTotalPages > 1 && (
            <div className="px-6 py-4 border-t flex items-center justify-between" style={{ borderColor: 'var(--border-color)' }}>
              <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Mostrando <span className="font-medium">{((userPage - 1) * userPageSize) + 1}</span> até{' '}
                <span className="font-medium">{Math.min(userPage * userPageSize, userTotal)}</span> de{' '}
                <span className="font-medium">{userTotal}</span> usuários
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setUserPage(1)}
                  disabled={userPage === 1}
                  className="px-3 py-1 text-sm rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                >
                  Primeira
                </button>
                <button
                  onClick={() => setUserPage(userPage - 1)}
                  disabled={userPage === 1}
                  className="px-3 py-1 text-sm rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                >
                  Anterior
                </button>
                <span className="px-3 py-1 text-sm rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}>
                  Página {userPage} de {userTotalPages}
                </span>
                <button
                  onClick={() => setUserPage(userPage + 1)}
                  disabled={userPage === userTotalPages}
                  className="px-3 py-1 text-sm rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                >
                  Próxima
                </button>
                <button
                  onClick={() => setUserPage(userTotalPages)}
                  disabled={userPage === userTotalPages}
                  className="px-3 py-1 text-sm rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                >
                  Última
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* MODIFICADO: Modal for Create/Edit Tenant - adaptado para tema dark */}
      {/* ANTES: bg-white dark:bg-white, text-gray-*, border-gray-* */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col" style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}>
            {/* MODIFICADO: Header Fixo com variáveis de tema */}
            {/* ANTES: border-gray-200, text-gray-900 dark:text-gray-900 */}
            <div className="px-6 py-4 border-b flex-shrink-0" style={{ borderColor: 'var(--border-color)' }}>
              <h3 className="text-xl font-semibold text-n-slate-12">
                {editingTenant ? 'Editar Empresa' : 'Criar Nova Empresa'}
              </h3>
            </div>

            {/* Conteúdo com Scroll */}
            <div className="overflow-y-auto flex-1">
              <form onSubmit={handleSubmitTenant} id="tenant-form" className="p-6">
                <div className="space-y-4">
                {/* MODIFICADO: Campo Nome da Empresa - cores adaptadas ao tema */}
                {/* ANTES: text-gray-700 dark:text-gray-700, border-gray-300, text-gray-900 dark:text-gray-900, text-gray-500 dark:text-gray-500 */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                    Nome da Empresa *
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500"
                    style={{ 
                      backgroundColor: theme === 'dark' ? '#1a1a1b' : '#ffffff', 
                      color: 'var(--text-primary)', 
                      border: '1px solid var(--border-color)'
                    }}
                    placeholder="Nome da empresa"
                    required
                  />
                  <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>O identificador único será gerado automaticamente</p>
                </div>

                {/* Relacionamento da empresa com o catálogo de plataformas */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="platformId" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                      Plataforma
                    </label>
                    <select
                      id="platformId"
                      value={formData.platformId || ''}
                      onChange={(e) => setFormData({ ...formData, platformId: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500"
                      style={{ 
                        backgroundColor: theme === 'dark' ? '#1a1a1b' : '#ffffff', 
                        color: 'var(--text-primary)', 
                        border: '1px solid var(--border-color)',
                        colorScheme: theme === 'dark' ? 'dark' : 'light'
                      }}
                    >
                      <option value="">Sem plataforma vinculada</option>
                      {platformOptions.map((platform) => (
                        <option key={platform.id} value={platform.id}>
                          {platform.name} — {platform.url}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="idPlataforma" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                      ID da conta na plataforma
                    </label>
                    <input
                      type="text"
                      id="idPlataforma"
                      value={formData.idPlataforma}
                      onChange={(e) => setFormData({ ...formData, idPlataforma: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500"
                      style={{ 
                        backgroundColor: theme === 'dark' ? '#1a1a1b' : '#ffffff', 
                        color: 'var(--text-primary)', 
                        border: '1px solid var(--border-color)' 
                      }}
                      placeholder="ID/identificador externo"
                    />
                  </div>
                </div>

                {/* MODIFICADO: Seção Permissões de WhatsApp - cores adaptadas ao tema */}
                {/* ANTES: border-gray-200, text-gray-900 dark:text-gray-900, text-gray-700 dark:text-gray-700, text-gray-500 dark:text-gray-500 */}
                <div className="border-t pt-4" style={{ borderColor: 'var(--border-color)' }}>
                  <h4 className="text-lg font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Permissões de WhatsApp</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <label htmlFor="allowWhatsWeb" className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                          WhatsApp Web
                        </label>
                        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Permite usar conexões do WhatsApp Web (WAHA/Evolution)</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          id="allowWhatsWeb"
                          checked={formData.allowWhatsWeb}
                          onChange={(e) => setFormData({ ...formData, allowWhatsWeb: e.target.checked })}
                          className="sr-only peer"
                        />
                        {/* MODIFICADO: Toggle switch adaptado - mantém funcionalidade mas ajusta cores conforme tema */}
                        {/* ANTES: bg-gray-200 dark:bg-gray-700, border-gray-300 dark:border-gray-600 */}
                        <div 
                          className="w-11 h-6 rounded-full peer peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all"
                          style={{ 
                            backgroundColor: formData.allowWhatsWeb ? '#2563eb' : (theme === 'dark' ? '#3f3f46' : '#e5e7eb'),
                            borderColor: theme === 'dark' ? '#52525b' : '#d1d5db'
                          }}
                        ></div>
                      </label>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <label htmlFor="allowOfficialApi" className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                          API Oficial WhatsApp
                        </label>
                        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Permite usar a API Oficial do WhatsApp Business</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          id="allowOfficialApi"
                          checked={formData.allowOfficialApi}
                          onChange={(e) => setFormData({ ...formData, allowOfficialApi: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div 
                          className="w-11 h-6 rounded-full peer peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all"
                          style={{ 
                            backgroundColor: formData.allowOfficialApi ? '#2563eb' : (theme === 'dark' ? '#3f3f46' : '#e5e7eb'),
                            borderColor: theme === 'dark' ? '#52525b' : '#d1d5db'
                          }}
                        ></div>
                      </label>
                    </div>
                  </div>
                </div>

                {/* MODIFICADO: Seção Limites e Quotas - cores adaptadas ao tema */}
                {/* ANTES: border-gray-200, text-gray-900 dark:text-gray-900, text-gray-700 dark:text-gray-700, border-gray-300, text-gray-500 dark:text-gray-500 */}
                <div className="border-t pt-4" style={{ borderColor: 'var(--border-color)' }}>
                  <h4 className="text-lg font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Limites e Quotas</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="maxUsers" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                        Máximo de Usuários *
                      </label>
                      <input
                        type="number"
                        id="maxUsers"
                        min="1"
                        value={formData.quotas.maxUsers}
                        onChange={(e) => setFormData({
                          ...formData,
                          quotas: { ...formData.quotas, maxUsers: e.target.value }
                        })}
                        className="w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500"
                        style={{ 
                          backgroundColor: theme === 'dark' ? '#1a1a1b' : '#ffffff', 
                          color: 'var(--text-primary)', 
                          border: '1px solid var(--border-color)' 
                        }}
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="maxConnections" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                        Máximo de Conexões *
                      </label>
                      <input
                        type="number"
                        id="maxConnections"
                        min="1"
                        value={formData.quotas.maxConnections}
                        onChange={(e) => setFormData({
                          ...formData,
                          quotas: { ...formData.quotas, maxConnections: e.target.value }
                        })}
                        className="w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500"
                        style={{ 
                          backgroundColor: theme === 'dark' ? '#1a1a1b' : '#ffffff', 
                          color: 'var(--text-primary)', 
                          border: '1px solid var(--border-color)' 
                        }}
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="maxContacts" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                        Máximo de Contatos *
                      </label>
                      <input
                        type="number"
                        id="maxContacts"
                        min="1"
                        value={formData.quotas.maxContacts}
                        onChange={(e) => setFormData({
                          ...formData,
                          quotas: { ...formData.quotas, maxContacts: e.target.value }
                        })}
                        className="w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500"
                        style={{ 
                          backgroundColor: theme === 'dark' ? '#1a1a1b' : '#ffffff', 
                          color: 'var(--text-primary)', 
                          border: '1px solid var(--border-color)' 
                        }}
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="maxCampaigns" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                        Máximo de Campanhas *
                      </label>
                      <input
                        type="number"
                        id="maxCampaigns"
                        min="1"
                        value={formData.quotas.maxCampaigns}
                        onChange={(e) => setFormData({
                          ...formData,
                          quotas: { ...formData.quotas, maxCampaigns: e.target.value }
                        })}
                        className="w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500"
                        style={{ 
                          backgroundColor: theme === 'dark' ? '#1a1a1b' : '#ffffff', 
                          color: 'var(--text-primary)', 
                          border: '1px solid var(--border-color)' 
                        }}
                        required
                      />
                    </div>
                  </div>

                  {/* Limite de Disparos */}
                  <div className="mt-4">
                    <label htmlFor="dispatchLimit" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                      Limite de Disparos Mensais
                    </label>
                    <input
                      type="number"
                      id="dispatchLimit"
                      min="0"
                      value={formData.quotas.dispatchLimit}
                      onChange={(e) => setFormData({
                        ...formData,
                        quotas: { ...formData.quotas, dispatchLimit: e.target.value }
                      })}
                      className="w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500"
                      style={{ 
                        backgroundColor: theme === 'dark' ? '#1a1a1b' : '#ffffff', 
                        color: 'var(--text-primary)', 
                        border: '1px solid var(--border-color)' 
                      }}
                      placeholder="Deixe vazio para ilimitado"
                    />
                    <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                      Deixe em branco ou zero para disparos ilimitados. Este valor representa o limite mensal de mensagens enviadas (Web + API Oficial).
                    </p>
                  </div>
                </div>

                {/* MODIFICADO: Seção Usuário Administrador - cores adaptadas ao tema */}
                {/* ANTES: border-gray-200, text-gray-900 dark:text-gray-900, text-gray-700 dark:text-gray-700, border-gray-300 */}
                {!editingTenant && (
                  <>
                    <div className="border-t pt-4" style={{ borderColor: 'var(--border-color)' }}>
                      <h4 className="text-lg font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>Usuário Administrador</h4>
                    </div>

                    <div>
                      <label htmlFor="adminNome" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                        Nome do Admin *
                      </label>
                      <input
                        type="text"
                        id="adminNome"
                        value={formData.adminUser.nome}
                        onChange={(e) => setFormData({
                          ...formData,
                          adminUser: { ...formData.adminUser, nome: e.target.value }
                        })}
                        className="w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500"
                        style={{ 
                          backgroundColor: theme === 'dark' ? '#1a1a1b' : '#ffffff', 
                          color: 'var(--text-primary)', 
                          border: '1px solid var(--border-color)' 
                        }}
                        placeholder="Nome completo"
                        required={!editingTenant}
                      />
                    </div>

                    <div>
                      <label htmlFor="adminEmail" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                        E-mail do Admin *
                      </label>
                      <input
                        type="email"
                        id="adminEmail"
                        value={formData.adminUser.email}
                        onChange={(e) => setFormData({
                          ...formData,
                          adminUser: { ...formData.adminUser, email: e.target.value }
                        })}
                        className="w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500"
                        style={{ 
                          backgroundColor: theme === 'dark' ? '#1a1a1b' : '#ffffff', 
                          color: 'var(--text-primary)', 
                          border: '1px solid var(--border-color)' 
                        }}
                        placeholder="admin@exemplo.com"
                        required={!editingTenant}
                      />
                    </div>

                    <div>
                      <label htmlFor="adminSenha" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                        Senha do Admin *
                      </label>
                      <div className="relative">
                        <input
                          type={showAdminPassword ? 'text' : 'password'}
                          id="adminSenha"
                          value={formData.adminUser.senha}
                          onChange={(e) => setFormData({
                            ...formData,
                            adminUser: { ...formData.adminUser, senha: e.target.value }
                          })}
                          className="w-full px-3 py-2 pr-10 rounded-lg focus:ring-2 focus:ring-blue-500"
                          style={{ 
                            backgroundColor: theme === 'dark' ? '#1a1a1b' : '#ffffff', 
                            color: 'var(--text-primary)', 
                            border: '1px solid var(--border-color)' 
                          }}
                          placeholder="Senha segura"
                          required={!editingTenant}
                          minLength={6}
                        />
                        <button
                          type="button"
                          onClick={() => setShowAdminPassword(!showAdminPassword)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 hover:opacity-70 focus:outline-none transition-opacity"
                          aria-label={showAdminPassword ? 'Ocultar senha' : 'Mostrar senha'}
                          style={{ color: 'var(--text-secondary)' }}
                        >
                          {showAdminPassword ? (
                            <EyeOff className="w-5 h-5" />
                          ) : (
                            <Eye className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
              </form>
            </div>

            {/* MODIFICADO: Footer Fixo com Botões - cores adaptadas ao tema */}
            {/* ANTES: border-gray-200, bg-white dark:bg-white, text-gray-700 dark:text-gray-700, bg-gray-100 hover:bg-gray-200 */}
            <div className="border-t px-6 py-4 flex-shrink-0" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-card)' }}>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium rounded-lg"
                  style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = theme === 'dark' ? '#3f3f46' : '#e5e7eb';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  form="tenant-form"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
                >
                  {editingTenant ? 'Atualizar' : 'Criar'} Empresa
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal for Create/Edit User */}
      {/* MODIFICADO: Modal de usuários adaptado para tema dark */}
      {/* ANTES: bg-white dark:bg-white, border-gray-200, text-gray-900 (cores fixas) */}
      {isUserModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div
            className="rounded-lg shadow-xl max-w-md w-full mx-4 transition-colors"
            style={{
              backgroundColor: 'var(--bg-card)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-color)'
            }}
          >
            <div
              className="px-6 py-4 border-b transition-colors"
              style={{ borderColor: 'var(--border-color)' }}
            >
              <h3
                className="text-lg font-semibold"
                style={{ color: 'var(--text-primary)' }}
              >
                {editingUser ? 'Editar Usuário' : 'Criar Novo Usuário'}
              </h3>
            </div>

            <form onSubmit={handleSubmitUser} className="p-6">
              <div className="space-y-4">
                {/* MODIFICADO: Campo Nome Completo adaptado para tema dark */}
                {/* ANTES: text-gray-700, border-gray-300, text-gray-900 (cores fixas) */}
                <div>
                  <label
                    htmlFor="userName"
                    className="block text-sm font-medium mb-1"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    id="userName"
                    value={userFormData.nome}
                    onChange={(e) => setUserFormData({ ...userFormData, nome: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    style={{
                      backgroundColor: theme === 'dark' ? '#1a1a1b' : 'white',
                      borderColor: 'var(--border-color)',
                      color: 'var(--text-primary)'
                    }}
                    placeholder="Nome completo do usuário"
                    required
                  />
                </div>

                {/* MODIFICADO: Campo E-mail adaptado para tema dark */}
                {/* ANTES: text-gray-700, border-gray-300, text-gray-900 (cores fixas) */}
                <div>
                  <label
                    htmlFor="userEmail"
                    className="block text-sm font-medium mb-1"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    E-mail *
                  </label>
                  <input
                    type="email"
                    id="userEmail"
                    value={userFormData.email}
                    onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    style={{
                      backgroundColor: theme === 'dark' ? '#1a1a1b' : 'white',
                      borderColor: 'var(--border-color)',
                      color: 'var(--text-primary)'
                    }}
                    placeholder="email@exemplo.com"
                    required
                  />
                </div>

                {/* MODIFICADO: Campo Senha adaptado para tema dark */}
                {/* ANTES: text-gray-700, border-gray-300, text-gray-900, text-gray-500 (cores fixas) */}
                <div>
                  <label
                    htmlFor="userPassword"
                    className="block text-sm font-medium mb-1"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    Senha {editingUser ? '(deixe vazio para manter)' : '*'}
                  </label>
                  <div className="relative">
                    <input
                      type={showUserPassword ? 'text' : 'password'}
                      id="userPassword"
                      value={userFormData.senha}
                      onChange={(e) => setUserFormData({ ...userFormData, senha: e.target.value })}
                      className="w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      style={{
                        backgroundColor: theme === 'dark' ? '#1a1a1b' : 'white',
                        borderColor: 'var(--border-color)',
                        color: 'var(--text-primary)'
                      }}
                      placeholder="Senha segura"
                      required={!editingUser}
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowUserPassword(!showUserPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 hover:opacity-70 focus:outline-none transition-opacity"
                      aria-label={showUserPassword ? 'Ocultar senha' : 'Mostrar senha'}
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      {showUserPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                  <p
                    className="text-xs mt-1"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    Deve conter pelo menos 6 caracteres, uma maiúscula, uma minúscula e um número
                  </p>
                </div>

                {/* MODIFICADO: Campo Nível de Acesso adaptado para tema dark */}
                {/* ANTES: text-gray-700, border-gray-300, text-gray-900, text-gray-500 (cores fixas) */}
                <div>
                  <label
                    htmlFor="userRole"
                    className="block text-sm font-medium mb-1"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    Nível de Acesso *
                  </label>
                  <select
                    id="userRole"
                    value={userFormData.role}
                    onChange={(e) => setUserFormData({
                      ...userFormData,
                      role: e.target.value as 'SUPERADMIN' | 'ADMIN' | 'USER'
                    })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    style={{
                      backgroundColor: theme === 'dark' ? '#1a1a1b' : 'white',
                      borderColor: 'var(--border-color)',
                      color: 'var(--text-primary)'
                    }}
                    required
                  >
                    <option value="USER">Usuário</option>
                    <option value="ADMIN">Administrador</option>
                    <option value="SUPERADMIN">Super Administrador (acesso global)</option>
                  </select>
                  <p
                    className="text-xs mt-1"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    {userFormData.role === 'SUPERADMIN'
                      ? 'Super Admin tem acesso a TODAS as empresas automaticamente. Você pode opcionalmente associá-lo a empresas específicas.'
                      : 'Defina o nível de acesso do usuário nas empresas selecionadas'}
                  </p>
                </div>

                {/* MODIFICADO: Seção Empresas adaptada para tema dark */}
                {/* ANTES: text-gray-700, text-gray-500, bg-gray-50, border-gray-200 (cores fixas) */}
                <div>
                  <label
                    className="block text-sm font-medium mb-2"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    Empresas {userFormData.role === 'SUPERADMIN' ? '(Opcional)' : '*'}
                  </label>

                  {availableTenants.length === 0 ? (
                    <div
                      className="text-sm p-4 rounded-lg border transition-colors"
                      style={{
                        color: 'var(--text-secondary)',
                        backgroundColor: theme === 'dark' ? '#1a1a1b' : '#f9fafb',
                        borderColor: 'var(--border-color)'
                      }}
                    >
                      Nenhuma empresa disponível
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {/* Campo de busca */}
                      <div className="relative">
                        {/* MODIFICADO: Campo de busca de empresas adaptado para tema dark */}
                        {/* ANTES: border-gray-300, text-gray-900 (cores fixas) */}
                        <input
                          type="text"
                          placeholder="Buscar empresa..."
                          value={tenantSearchQuery}
                          onChange={(e) => setTenantSearchQuery(e.target.value)}
                          className="w-full pl-9 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transition-colors"
                          style={{
                            backgroundColor: theme === 'dark' ? '#1a1a1b' : 'white',
                            borderColor: 'var(--border-color)',
                            color: 'var(--text-primary)'
                          }}
                        />
                        {/* MODIFICADO: Ícone de busca adaptado para tema dark */}
                        {/* ANTES: text-gray-400 (cor fixa) */}
                        <svg
                          className="absolute left-3 top-2.5 h-4 w-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          style={{ color: 'var(--text-secondary)' }}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                        {/* MODIFICADO: Botão de limpar busca adaptado para tema dark */}
                        {/* ANTES: text-gray-400, hover:text-gray-600 (cores fixas) */}
                        {tenantSearchQuery && (
                          <button
                            onClick={() => setTenantSearchQuery('')}
                            className="absolute right-3 top-2.5 transition-colors"
                            style={{
                              color: 'var(--text-secondary)'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = 'var(--text-primary)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = 'var(--text-secondary)';
                            }}
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>

                      {/* MODIFICADO: Container da lista de empresas adaptado para tema dark */}
                      {/* ANTES: border-gray-300, bg-white (cores fixas) */}
                      <div
                        className="border rounded-lg p-3 max-h-[250px] overflow-y-auto transition-colors"
                        style={{
                          borderColor: 'var(--border-color)',
                          backgroundColor: theme === 'dark' ? '#1a1a1b' : 'white'
                        }}
                      >
                        <div className="space-y-2">
                          {(() => {
                            const filteredTenants = availableTenants.filter(tenant =>
                              tenant.name.toLowerCase().includes(tenantSearchQuery.toLowerCase()) ||
                              tenant.slug.toLowerCase().includes(tenantSearchQuery.toLowerCase())
                            );

                            {/* MODIFICADO: Mensagem vazia adaptada para tema dark */}
                            {/* ANTES: text-gray-500, text-gray-400 (cores fixas) */}
                            if (filteredTenants.length === 0) {
                              return (
                                <div
                                  className="text-center py-8 text-sm"
                                  style={{ color: 'var(--text-secondary)' }}
                                >
                                  <svg
                                    className="mx-auto h-12 w-12 mb-2"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    style={{ color: 'var(--text-secondary)' }}
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                  </svg>
                                  Nenhuma empresa encontrada
                                </div>
                              );
                            }

                            {/* MODIFICADO: Item da lista de empresas adaptado para tema dark */}
                            {/* ANTES: bg-blue-50, border-blue-200, bg-gray-50, border-gray-200, text-gray-900, text-gray-500 (cores fixas) */}
                            return filteredTenants.map((tenant) => {
                              const isSelected = (userFormData.tenantIds || []).includes(tenant.id);
                              return (
                                <label
                                  key={tenant.id}
                                  className="flex items-center p-3 rounded-lg cursor-pointer transition-all border"
                                  style={{
                                    backgroundColor: isSelected
                                      ? (theme === 'dark' ? 'rgba(59, 130, 246, 0.2)' : '#eff6ff')
                                      : (theme === 'dark' ? '#1a1a1b' : '#f9fafb'),
                                    borderColor: isSelected
                                      ? (theme === 'dark' ? 'rgba(59, 130, 246, 0.3)' : '#bfdbfe')
                                      : 'var(--border-color)'
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isSelected) {
                                      e.currentTarget.style.backgroundColor = theme === 'dark' ? '#2a2a2f' : '#f3f4f6';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!isSelected) {
                                      e.currentTarget.style.backgroundColor = theme === 'dark' ? '#1a1a1b' : '#f9fafb';
                                    }
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={(e) => {
                                      const tenantIds = userFormData.tenantIds || [];
                                      if (e.target.checked) {
                                        setUserFormData({
                                          ...userFormData,
                                          tenantIds: [...tenantIds, tenant.id]
                                        });
                                      } else {
                                        setUserFormData({
                                          ...userFormData,
                                          tenantIds: tenantIds.filter(id => id !== tenant.id)
                                        });
                                      }
                                    }}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                  />
                                  <div className="ml-3 flex-1">
                                    <div className="flex items-center justify-between">
                                      <span
                                        className="text-sm font-medium"
                                        style={{
                                          color: isSelected
                                            ? (theme === 'dark' ? '#93c5fd' : '#1e40af')
                                            : 'var(--text-primary)'
                                        }}
                                      >
                                        {tenant.name}
                                      </span>
                                      <div className="flex flex-col items-end gap-1">
                                        <span
                                          className="text-xs px-2 py-0.5 rounded"
                                          style={{
                                            backgroundColor: tenant.active
                                              ? (theme === 'dark' ? 'rgba(34, 197, 94, 0.2)' : '#dcfce7')
                                              : (theme === 'dark' ? 'rgba(239, 68, 68, 0.2)' : '#fee2e2'),
                                            color: tenant.active
                                              ? (theme === 'dark' ? '#4ade80' : '#166534')
                                              : (theme === 'dark' ? '#f87171' : '#991b1b')
                                          }}
                                        >
                                          {tenant.active ? 'Ativa' : 'Inativa'}
                                        </span>
                                        {tenant.plataforma && (
                                          <span
                                            className="text-xs px-2 py-0.5 rounded font-medium"
                                            style={{
                                              backgroundColor: theme === 'dark' ? 'rgba(59, 130, 246, 0.2)' : '#dbeafe',
                                              color: theme === 'dark' ? '#93c5fd' : '#1e40af'
                                            }}
                                          >
                                            📱 {tenant.plataforma}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <span
                                      className="text-xs"
                                      style={{ color: 'var(--text-secondary)' }}
                                    >
                                      {tenant.slug}
                                    </span>
                                  </div>
                                </label>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* MODIFICADO: Textos de ajuda e contador adaptados para tema dark */}
                  {/* ANTES: text-gray-500, text-blue-600 (cores fixas) */}
                  <div className="mt-2 flex items-center justify-between">
                    <p
                      className="text-xs"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      {userFormData.role === 'SUPERADMIN'
                        ? 'Deixe vazio para acesso global a todas as empresas'
                        : 'Selecione pelo menos uma empresa'}
                    </p>
                    {(userFormData.tenantIds || []).length > 0 && (
                      <span
                        className="text-xs font-medium"
                        style={{ color: theme === 'dark' ? '#60a5fa' : '#2563eb' }}
                      >
                        {(userFormData.tenantIds || []).length} selecionada{(userFormData.tenantIds || []).length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>

                {/* MODIFICADO: Checkbox Usuário ativo adaptado para tema dark */}
                {/* ANTES: text-gray-700 (cor fixa) */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="userActive"
                    checked={userFormData.ativo}
                    onChange={(e) => setUserFormData({ ...userFormData, ativo: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="userActive"
                    className="ml-2 block text-sm"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    Usuário ativo
                  </label>
                </div>
              </div>

              {/* MODIFICADO: Botões do modal adaptados para tema dark */}
              {/* ANTES: text-gray-700, bg-gray-100, hover:bg-gray-200 (cores fixas) */}
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setIsUserModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium rounded-lg transition-colors"
                  style={{
                    backgroundColor: 'var(--bg-card)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-color)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = theme === 'dark' ? '#2a2a2f' : '#f3f4f6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--bg-card)';
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                >
                  {editingUser ? 'Atualizar' : 'Criar'} Usuário
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Integrations Tab */}
      {activeTab === 'integrations' && (
        <div className="rounded-lg shadow" style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}>
          <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
            <div>
              <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Integrações</h2>
              <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Configurações das APIs externas (Whats QR Code - API e Whats QR Code - Web)</p>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* WAHA Card */}
              <div
                onClick={() => setActiveModal('waha')}
                className="border-2 hover:border-green-300 rounded-lg p-6 cursor-pointer transition-all duration-200 flex flex-col items-center min-h-[180px] group"
                style={{ backgroundColor: theme === 'dark' ? '#2a2a2f' : '#f9fafb', borderColor: theme === 'dark' ? '#3f3f46' : '#e5e7eb' }}
              >
                <div className="flex-1 flex items-center justify-center">
                  <div className="w-28 h-16 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <img
                      src={wahaLogoSrc}
                      alt="WAHA Logo"
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
                <div className="mt-auto text-center">
                  <p className="text-xs mb-2" style={{ color: 'var(--text-secondary)' }}>WhatsApp HTTP API</p>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${integrationSettings?.wahaHost && integrationSettings?.wahaApiKey ? '' : ''}`}
                    style={{
                      backgroundColor: integrationSettings?.wahaHost && integrationSettings?.wahaApiKey
                        ? (theme === 'dark' ? '#166534' : '#dcfce7')
                        : (theme === 'dark' ? '#3f3f46' : '#f3f4f6'),
                      color: integrationSettings?.wahaHost && integrationSettings?.wahaApiKey
                        ? (theme === 'dark' ? '#86efac' : '#166534')
                        : (theme === 'dark' ? '#a1a1aa' : '#4b5563')
                    }}>
                    {integrationSettings?.wahaHost && integrationSettings?.wahaApiKey ? 'Configurado' : 'Não configurado'}
                  </span>
                </div>
              </div>

              {/* Evolution API Card */}
              <div
                onClick={() => setActiveModal('evolution')}
                className="border-2 hover:border-green-300 rounded-lg p-6 cursor-pointer transition-all duration-200 flex flex-col items-center min-h-[180px] group"
                style={{ backgroundColor: theme === 'dark' ? '#2a2a2f' : '#f9fafb', borderColor: theme === 'dark' ? '#3f3f46' : '#e5e7eb' }}
              >
                <div className="flex-1 flex items-center justify-center">
                  <div className="w-32 h-18 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <img
                      src={evolutionLogoSrc}
                      alt="Evolution API Logo"
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
                <div className="mt-auto text-center">
                  <p className="text-xs mb-2" style={{ color: 'var(--text-secondary)' }}>WhatsApp API Evolution</p>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${integrationSettings?.evolutionHost && integrationSettings?.evolutionApiKey ? '' : ''}`}
                    style={{
                      backgroundColor: integrationSettings?.evolutionHost && integrationSettings?.evolutionApiKey
                        ? (theme === 'dark' ? '#166534' : '#dcfce7')
                        : (theme === 'dark' ? '#3f3f46' : '#f3f4f6'),
                      color: integrationSettings?.evolutionHost && integrationSettings?.evolutionApiKey
                        ? (theme === 'dark' ? '#86efac' : '#166534')
                        : (theme === 'dark' ? '#a1a1aa' : '#4b5563')
                    }}>
                    {integrationSettings?.evolutionHost && integrationSettings?.evolutionApiKey ? 'Configurado' : 'Não configurado'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Tab */}
      {activeTab === 'ai' && (
        <div 
          className="rounded-lg shadow"
          style={{
            backgroundColor: 'var(--bg-card)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-color)'
          }}
        >
          <div 
            className="px-6 py-4 border-b"
            style={{ borderColor: 'var(--border-color)' }}
          >
            <div>
              <h2 
                className="text-lg font-semibold"
                style={{ color: 'var(--text-primary)' }}
              >
                🤖 Configurações de IA
              </h2>
              <p 
                className="text-sm mt-1"
                style={{ color: 'var(--text-secondary)' }}
              >
                Configure as API Keys para geração de mensagens por Inteligência Artificial
              </p>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* OpenAI Card */}
              <div
                onClick={() => setActiveModal('openai')}
                className="border-2 hover:border-green-300 rounded-lg p-6 cursor-pointer transition-all duration-200 flex flex-col items-center min-h-[180px] group"
                style={{
                  backgroundColor: theme === 'dark' ? '#2a2a2f' : '#f9fafb',
                  borderColor: theme === 'dark' ? '#3f3f46' : '#e5e7eb',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = theme === 'dark' ? '#35353a' : '#f3f4f6';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = theme === 'dark' ? '#2a2a2f' : '#f9fafb';
                }}
              >
                <div className="flex-1 flex items-center justify-center">
                  <div className="w-28 h-16 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <div className="text-4xl">🤖</div>
                  </div>
                </div>
                <div className="mt-auto text-center">
                  <p 
                    className="text-xs mb-2"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    OpenAI GPT
                  </p>
                  <span 
                    className="inline-flex px-2 py-1 text-xs font-semibold rounded-full"
                    style={{
                      backgroundColor: integrationSettings?.openaiApiKey
                        ? (theme === 'dark' ? '#166534' : '#dcfce7')
                        : (theme === 'dark' ? '#3f3f46' : '#f3f4f6'),
                      color: integrationSettings?.openaiApiKey
                        ? (theme === 'dark' ? '#86efac' : '#166534')
                        : (theme === 'dark' ? '#a1a1aa' : '#4b5563')
                    }}
                  >
                    {integrationSettings?.openaiApiKey ? 'Configurado' : 'Não configurado'}
                  </span>
                </div>
              </div>

              {/* Groq Card */}
              <div
                onClick={() => setActiveModal('groq')}
                className="border-2 hover:border-green-300 rounded-lg p-6 cursor-pointer transition-all duration-200 flex flex-col items-center min-h-[180px] group"
                style={{
                  backgroundColor: theme === 'dark' ? '#2a2a2f' : '#f9fafb',
                  borderColor: theme === 'dark' ? '#3f3f46' : '#e5e7eb',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = theme === 'dark' ? '#35353a' : '#f3f4f6';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = theme === 'dark' ? '#2a2a2f' : '#f9fafb';
                }}
              >
                <div className="flex-1 flex items-center justify-center">
                  <div className="w-28 h-16 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <div className="text-4xl">⚡</div>
                  </div>
                </div>
                <div className="mt-auto text-center">
                  <p 
                    className="text-xs mb-2"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    Groq LLaMA
                  </p>
                  <span 
                    className="inline-flex px-2 py-1 text-xs font-semibold rounded-full"
                    style={{
                      backgroundColor: integrationSettings?.groqApiKey
                        ? (theme === 'dark' ? '#166534' : '#dcfce7')
                        : (theme === 'dark' ? '#3f3f46' : '#f3f4f6'),
                      color: integrationSettings?.groqApiKey
                        ? (theme === 'dark' ? '#86efac' : '#166534')
                        : (theme === 'dark' ? '#a1a1aa' : '#4b5563')
                    }}
                  >
                    {integrationSettings?.groqApiKey ? 'Configurado' : 'Não configurado'}
                  </span>
                </div>
              </div>
            </div>

            <div 
              className="mt-8 p-4 rounded-lg"
              style={{
                backgroundColor: theme === 'dark' ? '#1e3a5f' : '#eff6ff',
                border: `1px solid ${theme === 'dark' ? '#2d4a6b' : '#bfdbfe'}`,
              }}
            >
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div 
                    className="text-xl"
                    style={{ color: theme === 'dark' ? '#60a5fa' : '#60a5fa' }}
                  >
                    ℹ️
                  </div>
                </div>
                <div className="ml-3">
                  <h3 
                    className="text-sm font-medium"
                    style={{ color: theme === 'dark' ? '#bfdbfe' : '#1e40af' }}
                  >
                    Centralização de API Keys
                  </h3>
                  <div 
                    className="mt-2 text-sm"
                    style={{ color: theme === 'dark' ? '#93c5fd' : '#1e3a8a' }}
                  >
                    <p>
                      As API Keys de IA são configuradas globalmente pelo SUPERADMIN e compartilhadas por todos os tenants.
                      Isso simplifica o gerenciamento e garante que todos os usuários tenham acesso às funcionalidades de IA.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Backup Tab */}
      {activeTab === 'backup' && (
        <SystemBackup />
      )}


      {/* General Settings Tab */}
      {activeTab === 'general' && (
        <div className="max-w-2xl mx-auto">
          <div 
            className="rounded-lg shadow p-5"
            style={{
              backgroundColor: 'var(--bg-card)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-color)'
            }}
          >
            <h2 
              className="text-lg font-semibold mb-6"
              style={{ color: 'var(--text-primary)' }}
            >
              🏢 Informações da Empresa
            </h2>

            <form onSubmit={handleGeneralSubmit(onGeneralSubmit)} className="space-y-5">
              <div>
                <label 
                  htmlFor="companyName" 
                  className="block text-sm font-normal mb-2"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Nome da Empresa *
                </label>
                <input
                  id="companyName"
                  type="text"
                  {...registerGeneral('companyName')}
                  placeholder="Nome da sua empresa"
                  className="w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{
                    backgroundColor: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-color)',
                    boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.05)'
                  }}
                />
                {generalErrors.companyName && (
                  <p className="text-red-500 text-sm mt-1">
                    {generalErrors.companyName.message}
                  </p>
                )}
              </div>

              <div>
                <label 
                  htmlFor="pageTitle" 
                  className="block text-sm font-normal mb-2"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Título da Página *
                </label>
                <input
                  id="pageTitle"
                  type="text"
                  {...registerGeneral('pageTitle')}
                  placeholder="Ex: Konverte Digital - Gestão de Contatos"
                  className="w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{
                    backgroundColor: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-color)',
                    boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.05)'
                  }}
                />
                {generalErrors.pageTitle && (
                  <p className="text-red-500 text-sm mt-1">
                    {generalErrors.pageTitle.message}
                  </p>
                )}
                <p 
                  className="text-xs mt-1"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Título que aparecerá na aba do navegador
                </p>
              </div>

              <div className="flex justify-end items-center pt-2">
                {generalSaveMessage && (
                  <p 
                    className="text-sm mr-4"
                    style={{ 
                      color: generalSaveMessage.type === 'success' ? 'var(--success)' : 'var(--error)'
                    }}
                  >
                    {generalSaveMessage.text}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={isGeneralSubmitting}
                  className="bg-blue-600 text-white py-2 px-6 rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {isGeneralSubmitting ? 'Salvando...' : 'Salvar Informações'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Appearance Tab */}
      {activeTab === 'appearance' && (
        <div className="space-y-6">
          {/* Logo da Empresa */}
          <div 
            className="rounded-lg shadow p-6"
            style={{
              backgroundColor: 'var(--bg-card)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-color)'
            }}
          >
            <h2 
              className="text-lg font-semibold mb-4"
              style={{ color: 'var(--text-primary)' }}
            >
              🎨 Logo da Empresa
            </h2>

            {/* Logo Atual */}
            {generalSettings?.logoUrl && (
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Logo Atual:</p>
                <div className="flex items-center space-x-4">
                  <img
                    src={generalSettings.logoUrl}
                    alt="Logo atual"
                    className="w-20 h-20 object-contain border border-gray-200 rounded-lg"
                  />
                  <button
                    onClick={removeLogo}
                    className="inline-flex items-center justify-center h-6 w-6 p-0 rounded-lg transition-all duration-200 ease-in-out focus:outline-none"
                    title="Remover"
                    aria-label="Remover logo"
                    style={{
                      background: 'rgba(229, 70, 102, 0.15)',
                      color: (theme === 'dark' ? '#FF949D' : '#CA244D')
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(229, 70, 102, 0.2)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(229, 70, 102, 0.15)'; }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden="true">
                      <path d="M3 6h18" />
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                      <path d="M10 11v6" />
                      <path d="M14 11v6" />
                      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* Upload Nova Logo */}
            <div className="space-y-4">
              <FileUpload
                id="logo"
                label="Nova Logo"
                accept="image/*"
                maxSizeMb={5}
                description="Formatos aceitos: JPEG, PNG, GIF, WebP (max 5MB)"
                value={logoFile}
                previewUrl={logoPreview}
                onChange={(file) => {
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                      setLogoPreview(e.target?.result as string);
                    };
                    reader.readAsDataURL(file);
                    setLogoFile(file);
                  } else {
                    setLogoFile(null);
                    setLogoPreview('');
                  }
                }}
              />

              {logoFile && (
                <div className="flex items-center gap-4">
                  <button
                    onClick={uploadLogo}
                    disabled={uploadingLogo}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                  >
                    {uploadingLogo ? 'Carregando...' : 'Carregar Logo'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* MODIFICADO: Card Ícone Geral adaptado ao tema */}
          {/* ANTES: className="bg-white" e textos com text-gray-* */}
          <div
            className="rounded-lg shadow p-6"
            style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
          >
            <h2 className="text-xl font-semibold mb-4 text-n-slate-12">
              🏷️ Ícone Geral
            </h2>
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
              Ícone usado na página de login e no menu interno do sistema
            </p>

            {/* Ícone Atual */}
            {generalSettings?.iconUrl && (
              <div className="mb-4">
                {/* MODIFICADO: texto com variáveis de cor */}
                {/* ANTES: text-gray-700 */}
                <p className="text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Ícone Atual:</p>
                <div className="flex items-center space-x-4">
                  <img
                    src={generalSettings.iconUrl}
                    alt="Ícone atual"
                    className="w-12 h-12 object-contain rounded"
                    style={{ border: '1px solid var(--border-color)' }}
                  />
                  <button
                    onClick={removeIcon}
                    className="inline-flex items-center justify-center h-6 w-6 p-0 rounded-lg transition-all duration-200 ease-in-out focus:outline-none"
                    title="Remover"
                    aria-label="Remover ícone"
                    style={{
                      background: 'rgba(229, 70, 102, 0.15)',
                      color: (theme === 'dark' ? '#FF949D' : '#CA244D')
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(229, 70, 102, 0.2)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(229, 70, 102, 0.15)'; }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden="true">
                      <path d="M3 6h18" />
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a 2 2 0 0 1-2-2L5 6" />
                      <path d="M10 11v6" />
                      <path d="M14 11v6" />
                      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v 2" />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* Upload Novo Ícone */}
            <div className="space-y-4">
              <FileUpload
                id="icon"
                label="Novo Ícone"
                accept="image/*"
                maxSizeMb={5}
                description="Recomendado: PNG, 64x64px ou similar (max 5MB)"
                value={iconFile}
                previewUrl={iconPreview}
                onChange={(file) => {
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (e) => setIconPreview(e.target?.result as string);
                    reader.readAsDataURL(file);
                    setIconFile(file);
                  } else {
                    setIconFile(null);
                    setIconPreview('');
                  }
                }}
              />

              {iconFile && (
                <div className="flex items-center gap-4">
                  <button
                    onClick={uploadIcon}
                    disabled={uploadingIcon}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                  >
                    {uploadingIcon ? 'Carregando...' : 'Carregar Ícone'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* MODIFICADO: Card Favicon adaptado ao tema */}
          {/* ANTES: bg-white e textos com text-gray-* */}
          <div
            className="rounded-lg shadow p-6"
            style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
          >
            <h2 className="text-xl font-semibold mb-4 text-n-slate-12">
              🌐 Favicon da Plataforma
            </h2>

            {/* Favicon Atual */}
            {generalSettings?.faviconUrl && (
              <div className="mb-4">
                {/* MODIFICADO: texto com variáveis */}
                {/* ANTES: text-gray-700 */}
                <p className="text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Favicon Atual:</p>
                <div className="flex items-center space-x-4">
                  <img
                    src={generalSettings.faviconUrl}
                    alt="Favicon atual"
                    className="w-8 h-8 object-contain rounded"
                    style={{ border: '1px solid var(--border-color)' }}
                  />
                  <button
                    onClick={removeFavicon}
                    className="inline-flex items-center justify-center h-6 w-6 p-0 rounded-lg transition-all duration-200 ease-in-out focus:outline-none"
                    title="Remover"
                    aria-label="Remover favicon"
                    style={{
                      background: 'rgba(229, 70, 102, 0.15)',
                      color: (theme === 'dark' ? '#FF949D' : '#CA244D')
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(229, 70, 102, 0.2)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(229, 70, 102, 0.15)'; }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden="true">
                      <path d="M3 6h18" />
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                      <path d="M10 11v6" />
                      <path d="M14 11v6" />
                      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* Upload Novo Favicon */}
            <div className="space-y-4">
              <FileUpload
                id="favicon"
                label="Novo Favicon"
                accept="image/*"
                maxSizeMb={5}
                description="Formatos aceitos: PNG, ICO (recomendado 32x32px ou 16x16px)"
                value={faviconFile}
                previewUrl={faviconPreview}
                onChange={(file) => {
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (e) => setFaviconPreview(e.target?.result as string);
                    reader.readAsDataURL(file);
                    setFaviconFile(file);
                  } else {
                    setFaviconFile(null);
                    setFaviconPreview('');
                  }
                }}
              />

              {faviconFile && (
                <div className="flex items-center gap-4">
                  <button
                    onClick={uploadFavicon}
                    disabled={uploadingFavicon}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                  >
                    {uploadingFavicon ? 'Carregando...' : 'Carregar Favicon'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal WAHA */}
      {activeModal === 'waha' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="rounded-lg p-6 w-full max-w-md mx-4" style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-n-slate-12">🔗 Configurar Whats QR Code - API</h3>
              <button
                onClick={() => setActiveModal(null)}
                className="text-2xl"
                style={{ color: 'var(--text-secondary)' }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleFormSubmit(onIntegrationSubmit)} className="space-y-4">
              <div>
                <label htmlFor="wahaHost" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                  Host Whats QR Code - API *
                </label>
                <input
                  id="wahaHost"
                  type="url"
                  {...register('wahaHost')}
                  placeholder="https://waha.exemplo.com.br"
                  className="w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                />
                {errors.wahaHost && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.wahaHost.message}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="wahaApiKey" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                  API Key Whats QR Code - API *
                </label>
                <div className="relative">
                  <input
                    id="wahaApiKey"
                    type={showWahaApiKey ? 'text' : 'password'}
                    {...register('wahaApiKey')}
                    placeholder="sua-api-key-aqui"
                    className="w-full px-3 py-2 pr-10 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowWahaApiKey(!showWahaApiKey)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 hover:opacity-70 focus:outline-none transition-opacity"
                    aria-label={showWahaApiKey ? 'Ocultar API Key' : 'Mostrar API Key'}
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    {showWahaApiKey ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {errors.wahaApiKey && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.wahaApiKey.message}
                  </p>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setActiveModal(null)}
                  className="flex-1 px-4 py-2 rounded-md"
                  style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  {isSubmitting ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Evolution API */}
      {activeModal === 'evolution' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="rounded-lg p-6 w-full max-w-md mx-4" style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-n-slate-12">🚀 Configurar Whats QR Code - Web</h3>
              <button
                onClick={() => setActiveModal(null)}
                className="text-2xl"
                style={{ color: 'var(--text-secondary)' }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleFormSubmit(onIntegrationSubmit)} className="space-y-4">
              <div>
                <label htmlFor="evolutionHost" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                  Host Evolution *
                </label>
                <input
                  id="evolutionHost"
                  type="url"
                  {...register('evolutionHost')}
                  placeholder="https://evolution.exemplo.com.br"
                  className="w-full px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                />
                {errors.evolutionHost && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.evolutionHost.message}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="evolutionApiKey" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                  API Key Evolution *
                </label>
                <div className="relative">
                  <input
                    id="evolutionApiKey"
                    type={showEvolutionApiKey ? 'text' : 'password'}
                    {...register('evolutionApiKey')}
                    placeholder="sua-api-key-evolution-aqui"
                    className="w-full px-3 py-2 pr-10 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowEvolutionApiKey(!showEvolutionApiKey)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 hover:opacity-70 focus:outline-none transition-opacity"
                    aria-label={showEvolutionApiKey ? 'Ocultar API Key' : 'Mostrar API Key'}
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    {showEvolutionApiKey ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {errors.evolutionApiKey && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.evolutionApiKey.message}
                  </p>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setActiveModal(null)}
                  className="flex-1 px-4 py-2 rounded-md"
                  style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {isSubmitting ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal OpenAI */}
      {activeModal === 'openai' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="rounded-lg p-6 w-full max-w-md mx-4" style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>🤖 Configurar OpenAI</h3>
              <button onClick={() => setActiveModal(null)} className="text-2xl" style={{ color: 'var(--text-secondary)' }}>✕</button>
            </div>

            <form onSubmit={handleFormSubmit(onOpenAISubmit)} className="space-y-4">
              <div>
                <label htmlFor="openaiApiKey" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                  API Key OpenAI
                </label>
                <div className="relative">
                  <input
                    id="openaiApiKey"
                    type={showOpenAIApiKey ? 'text' : 'password'}
                    {...register('openaiApiKey')}
                    placeholder="sk-..."
                    className="w-full px-3 py-2 pr-10 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowOpenAIApiKey(!showOpenAIApiKey)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 hover:opacity-70 focus:outline-none transition-opacity"
                    aria-label={showOpenAIApiKey ? 'Ocultar API Key' : 'Mostrar API Key'}
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    {showOpenAIApiKey ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {errors.openaiApiKey && (
                  <p className="text-red-500 text-sm mt-1">{errors.openaiApiKey.message}</p>
                )}
                <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                  Chave API para integração com OpenAI GPT nas campanhas (modelos avançados de IA)
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setActiveModal(null)} className="flex-1 px-4 py-2 text-sm font-medium rounded-md" style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}>
                  Cancelar
                </button>
                {integrationSettings?.openaiApiKey && (
                  <button type="button" onClick={() => removeApiKey('openai')} disabled={isSubmitting} className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50">
                    Remover
                  </button>
                )}
                <button type="submit" disabled={isSubmitting} className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50">
                  {isSubmitting ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Groq */}
      {activeModal === 'groq' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="rounded-lg p-6 w-full max-w-md mx-4" style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>⚡ Configurar Groq</h3>
              <button onClick={() => setActiveModal(null)} className="text-2xl" style={{ color: 'var(--text-secondary)' }}>✕</button>
            </div>

            <form onSubmit={handleFormSubmit(onGroqSubmit)} className="space-y-4">
              <div>
                <label htmlFor="groqApiKey" className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                  API Key Groq
                </label>
                <div className="relative">
                  <input
                    id="groqApiKey"
                    type={showGroqApiKey ? 'text' : 'password'}
                    {...register('groqApiKey')}
                    placeholder="gsk_..."
                    className="w-full px-3 py-2 pr-10 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowGroqApiKey(!showGroqApiKey)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 hover:opacity-70 focus:outline-none transition-opacity"
                    aria-label={showGroqApiKey ? 'Ocultar API Key' : 'Mostrar API Key'}
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    {showGroqApiKey ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {errors.groqApiKey && (
                  <p className="text-red-500 text-sm mt-1">{errors.groqApiKey.message}</p>
                )}
                <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                  Chave API para integração com Groq AI nas campanhas (modelos rápidos e eficientes)
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setActiveModal(null)} className="flex-1 px-4 py-2 text-sm font-medium rounded-md" style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}>
                  Cancelar
                </button>
                {integrationSettings?.groqApiKey && (
                  <button type="button" onClick={() => removeApiKey('groq')} disabled={isSubmitting} className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50">
                    Remover
                  </button>
                )}
                <button type="submit" disabled={isSubmitting} className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50">
                  {isSubmitting ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
    </>
  );
}

interface TooltipFormProps {
  tooltip: Tooltip;
  theme: string;
  onSave: (fields: Partial<Tooltip>) => void;
  onUploadAttachment: (file: File) => void;
  onRemoveAttachment: () => void;
  saving: boolean;
  uploading: boolean;
}

const TooltipForm: React.FC<TooltipFormProps> = ({
  tooltip,
  theme,
  onSave,
  onUploadAttachment,
  onRemoveAttachment,
  saving,
  uploading
}) => {
  const [mensagem, setMensagem] = useState(tooltip.mensagem);
  const [linkSuporte, setLinkSuporte] = useState(tooltip.linkSuporte || '');
  const [videoPlataforma, setVideoPlataforma] = useState(tooltip.videoPlataforma || 'none');
  const [videoTitulo, setVideoTitulo] = useState(tooltip.videoTitulo || '');
  const [videoUrl, setVideoUrl] = useState(tooltip.videoUrl || '');
  const [videoDescricao, setVideoDescricao] = useState(tooltip.videoDescricao || '');

  const hasAttachment = !!tooltip.videoAnexoUrl;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!mensagem.trim()) {
      toast.error('O campo de mensagem principal é obrigatório');
      return;
    }
    onSave({
      mensagem: mensagem.trim(),
      linkSuporte: linkSuporte.trim() || null,
      videoPlataforma: videoPlataforma === 'none' ? null : videoPlataforma,
      videoTitulo: videoPlataforma === 'none' ? null : videoTitulo.trim() || null,
      videoUrl: videoPlataforma === 'none' ? null : videoUrl.trim() || null,
      videoDescricao: videoPlataforma === 'none' ? null : videoDescricao.trim() || null
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('O arquivo deve ter no máximo 10MB');
        return;
      }
      onUploadAttachment(file);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="px-6 py-5 bg-gray-50/50 dark:bg-black/20 border-t space-y-4 text-sm" style={{ borderColor: 'var(--border-color-soft)' }}>
      {/* Campo Mensagem */}
      <div>
        <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>
          Mensagem Principal *
        </label>
        <textarea
          rows={3}
          value={mensagem}
          onChange={e => setMensagem(e.target.value)}
          placeholder="Texto descritivo principal que explica a funcionalidade..."
          className="w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
          style={{
            backgroundColor: theme === 'dark' ? '#18181b' : '#ffffff',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-color)'
          }}
          required
        />
      </div>

      {/* Link de Suporte */}
      <div>
        <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>
          Link de Suporte (opcional)
        </label>
        <input
          type="url"
          value={linkSuporte}
          onChange={e => setLinkSuporte(e.target.value)}
          placeholder="https://exemplo.com.br/central-de-ajuda"
          className="w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
          style={{
            backgroundColor: theme === 'dark' ? '#18181b' : '#ffffff',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-color)'
          }}
        />
      </div>

      {/* Grid Vídeo Aula */}
      <div className="border-t pt-4 space-y-4" style={{ borderColor: 'var(--border-color-soft)' }}>
        <h4 className="font-semibold text-xs text-blue-500 uppercase tracking-wider">Videoaula & Materiais Complementares</h4>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>
              Plataforma do Vídeo
            </label>
            <select
              value={videoPlataforma}
              onChange={e => setVideoPlataforma(e.target.value)}
              className="w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
              style={{
                backgroundColor: theme === 'dark' ? '#18181b' : '#ffffff',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-color)',
                colorScheme: theme === 'dark' ? 'dark' : 'light'
              }}
            >
              <option value="none">Sem vídeoaula</option>
              <option value="youtube">YouTube</option>
              <option value="pandavideo">Panda Videos</option>
            </select>
          </div>

          {videoPlataforma !== 'none' && (
            <>
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>
                  Título da Aula
                </label>
                <input
                  type="text"
                  value={videoTitulo}
                  onChange={e => setVideoTitulo(e.target.value)}
                  placeholder="Ex: Como importar contatos passo a passo"
                  className="w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  style={{
                    backgroundColor: theme === 'dark' ? '#18181b' : '#ffffff',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-color)'
                  }}
                />
              </div>

              <div className="md:col-span-3">
                <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>
                  URL do Vídeo / Embed do Iframe
                </label>
                <input
                  type="text"
                  value={videoUrl}
                  onChange={e => setVideoUrl(e.target.value)}
                  placeholder="Cole a URL do vídeo ou o iframe completo do Panda/YouTube"
                  className="w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none font-mono text-xs"
                  style={{
                    backgroundColor: theme === 'dark' ? '#18181b' : '#ffffff',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-color)'
                  }}
                />
              </div>

              <div className="md:col-span-3">
                <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>
                  Descrição da Videoaula
                </label>
                <textarea
                  rows={2}
                  value={videoDescricao}
                  onChange={e => setVideoDescricao(e.target.value)}
                  placeholder="Explicação complementar sobre o conteúdo abordado no vídeo..."
                  className="w-full px-3 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  style={{
                    backgroundColor: theme === 'dark' ? '#18181b' : '#ffffff',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-color)'
                  }}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Upload de Anexo */}
      <div className="border-t pt-4 space-y-2" style={{ borderColor: 'var(--border-color-soft)' }}>
        <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>
          Material Complementar (Anexo)
        </label>
        
        {hasAttachment ? (
          <div className="flex items-center justify-between p-3 rounded-lg border" style={{ backgroundColor: 'var(--bg-primary)', borderColor: 'var(--border-color)' }}>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>
                📎 {tooltip.videoAnexoNome}
              </span>
            </div>
            <button
              type="button"
              onClick={onRemoveAttachment}
              className="text-xs text-red-500 hover:text-red-600 font-semibold cursor-pointer border-0 bg-transparent"
            >
              Remover Material
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <input
              type="file"
              accept=".csv,.xls,.xlsx,.pdf,.png,.jpg,.jpeg,.webp"
              onChange={handleFileChange}
              id={`attachment-upload-${tooltip.id}`}
              className="hidden"
              disabled={uploading}
            />
            <label
              htmlFor={`attachment-upload-${tooltip.id}`}
              className={`px-4 py-2 border rounded-lg text-xs font-semibold cursor-pointer transition-colors ${
                uploading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              style={{
                backgroundColor: 'var(--bg-card)',
                borderColor: 'var(--border-color)',
                color: 'var(--text-primary)'
              }}
            >
              {uploading ? 'Carregando arquivo...' : 'Escolher arquivo (Max 10MB)'}
            </label>
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
              Formatos aceitos: CSV, Excel, PDF, Imagens
            </span>
          </div>
        )}
      </div>

      {/* Ações */}
      <div className="flex justify-end border-t pt-3" style={{ borderColor: 'var(--border-color-soft)' }}>
        <button
          type="submit"
          disabled={saving}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold transition-colors disabled:opacity-50"
        >
          {saving ? 'Salvando...' : 'Salvar Alterações'}
        </button>
      </div>
    </form>
  );
};
